import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Heart, ArrowLeft, Plus, Trash2, FileText, ClipboardCheck, 
  Sparkles, ChevronDown, ChevronUp, Calendar, Edit, Eye,
  Loader2, Copy, Printer, Download, X, Save, AlertCircle, BookOpen,
  CheckCircle2, Circle, Clock, ListChecks, PlayCircle, Archive, TrendingUp, AlertTriangle, Upload, FileDown, RefreshCw
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import IntroTutorial from '@/components/IntroTutorial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Assessment types
const ASSESSMENT_TYPES = [
  { value: 'initial', label: 'Initial Evaluation' },
  { value: 'progress', label: 'Progress Assessment' },
  { value: 'annual_review', label: 'Annual Review' },
  { value: 'discharge', label: 'Discharge Assessment' }
];

// Non-standardized measure types
const MEASURE_TYPES = [
  { value: 'language_sample', label: 'Language Sample Analysis' },
  { value: 'observation', label: 'Clinical Observation' },
  { value: 'criterion_referenced', label: 'Criterion-Referenced Test' },
  { value: 'informal_assessment', label: 'Informal Assessment' },
  { value: 'play_based', label: 'Play-Based Assessment' },
  { value: 'parent_report', label: 'Parent/Caregiver Report' }
];

// Guided prompts for informal assessment by clinical area
// These help clinicians know what to observe and document
const INFORMAL_ASSESSMENT_PROMPTS = {
  prelinguistic: {
    title: 'Prelinguistic Skills',
    prompts: [
      'Eye contact and gaze patterns',
      'Joint attention (initiating and responding)',
      'Turn-taking in play and interaction',
      'Vocal and motor imitation',
      'Communicative intent and gestures',
      'Response to name and environmental sounds',
      'Use of pointing (protoimperative vs protodeclarative)'
    ],
    example: 'Example: "Made eye contact when calling his name 3/5 times. Initiated joint attention by holding up toy car and vocalizing. Did not point to request but pulled adult\'s hand toward desired items. Imitated motor actions (clapping, waving) but limited vocal imitation."'
  },
  receptive: {
    title: 'Receptive Language',
    prompts: [
      'Understanding of single words in context',
      'Following 1-step and 2-step directions',
      'Comprehension of basic concepts (in/on/under, big/little)',
      'Understanding of questions (what, where, who)',
      'Response to yes/no questions',
      'Identification of objects, pictures, body parts',
      'Understanding of action words and descriptors'
    ],
    example: 'Example: "Followed 1-step directions with visual cues (e.g., \'put in\') but needed repetition for 2-step directions. Identified 6/10 body parts. Understood \'what\' questions but confused by \'where\' questions. Responded to \'no\' but not \'stop\' without gesture."'
  },
  auditory: {
    title: 'Auditory Processing',
    prompts: [
      'Attention to auditory input',
      'Sound discrimination (similar sounds)',
      'Auditory memory (sequences, instructions)',
      'Following directions in noise vs quiet',
      'Auditory closure (filling in missing information)',
      'Localization of sound sources',
      'Processing speed for verbal information'
    ],
    example: 'Example: "Attended to auditory input for approximately 5 minutes before becoming distracted. Required repetition of multi-step instructions. Performance decreased noticeably in background noise. Correctly localized sounds from left/right but not behind."'
  },
  expressive: {
    title: 'Expressive Language',
    prompts: [
      'Spontaneous language use and initiations',
      'Vocabulary diversity and word finding',
      'Sentence length and complexity (MLU)',
      'Use of gestalts vs analytical language',
      'Grammar and morphology patterns',
      'Narrative and storytelling ability',
      'Requesting, commenting, and protesting'
    ],
    example: 'Example: "MLU approximately 3.2 words. Used gestalts from TV shows (\'Let\'s go!\' from Dora) appropriately to initiate. Some word-finding pauses noted. Omitted past tense -ed and plural -s consistently. Could describe immediate events but struggled with sequencing a simple story."'
  },
  literacy: {
    title: 'Literacy Skills',
    prompts: [
      'Phonological awareness (rhyming, syllables)',
      'Letter knowledge and sound-symbol association',
      'Print awareness and book handling',
      'Interest in reading activities',
      'Emergent writing attempts',
      'Listening comprehension of stories',
      'Retelling familiar stories'
    ],
    example: 'Example: "Recognized 15/26 uppercase letters. Could clap syllables in 2-syllable words. Did not yet identify rhyming words. Held book correctly and turned pages front to back. Attempted to write name - wrote first 2 letters correctly."'
  },
  social_emotional: {
    title: 'Social-Emotional Skills',
    prompts: [
      'Self-advocacy and expressing needs',
      'Emotional regulation strategies',
      'Perspective-taking and empathy',
      'Peer interaction patterns',
      'Response to transitions and change',
      'Coping with frustration',
      'Attachment and engagement with caregivers'
    ],
    example: 'Example: "Expressed needs by leading adult to items or using single words. Became dysregulated during transitions - needed 5-minute warning and visual timer. Parallel play observed with peers but did not initiate interaction. Used deep breathing (with prompting) to calm after frustration."'
  },
  speech_motor: {
    title: 'Speech & Motor Speech',
    prompts: [
      'Speech sound inventory and patterns',
      'Stimulability for error sounds',
      'Intelligibility to familiar vs unfamiliar listeners',
      'Oral motor structure and function',
      'Diadochokinetic rates (pa-ta-ka)',
      'Consistency of productions',
      'Prosody and intonation patterns'
    ],
    example: 'Example: "Intelligible approximately 70% to familiar listeners, 40% to unfamiliar. Fronting of velars (/k/→[t], /g/→[d]) consistent. Stimulable for /k/ in isolation with tactile cue. Oral structure WNL. Variable productions of multisyllabic words noted."'
  },
  aac: {
    title: 'AAC Assessment',
    prompts: [
      'Current communication modes used',
      'Symbol recognition and understanding',
      'Motor access method effectiveness',
      'Navigation of AAC system',
      'Communication rate and efficiency',
      'Use across different partners/contexts',
      'Vocabulary needs and gaps'
    ],
    example: 'Example: "Currently uses LAMP Words for Life on iPad with direct selection. Navigates to core vocabulary independently. Requires prompting to access fringe vocabulary. Communication rate approximately 5 words/minute. Uses device primarily with therapist and mother - less with peers."'
  },
  feeding: {
    title: 'Feeding & Swallowing',
    prompts: [
      'Oral motor skills for feeding',
      'Texture tolerance and preferences',
      'Self-feeding abilities',
      'Mealtime behaviors and engagement',
      'Signs of aspiration or difficulty',
      'Sensory factors affecting eating',
      'Caregiver-child interaction during meals'
    ],
    example: 'Example: "Accepts purees and soft solids. Refuses crunchy textures - gags on crackers. Self-feeds with spoon with some spillage. Meals last approximately 45 minutes with frequent breaks. No overt signs of aspiration observed. Prefers bland foods - rejects strong flavors."'
  },
  pragmatics: {
    title: 'Pragmatic Language',
    prompts: [
      'Topic initiation and maintenance',
      'Conversational turn-taking',
      'Use of eye contact during communication',
      'Understanding nonverbal cues',
      'Narrative discourse skills',
      'Register variation (formal vs informal)',
      'Repair strategies when misunderstood'
    ],
    example: 'Example: "Initiated conversation about preferred topics (trains) but difficulty maintaining other topics. Made eye contact when speaking but less when listening. Did not adjust language for different partners. When misunderstood, repeated same utterance louder rather than rephrasing."'
  }
};

// Report styles
const REPORT_STYLES = [
  { value: 'parent_friendly', label: 'Parent-Friendly Summary', description: 'Warm, accessible language for families (Recommended)' },
  { value: 'summary', label: 'Summary Report', description: 'Concise 1-2 page clinical summary' },
  { value: 'comprehensive', label: 'Comprehensive Clinical Report', description: 'Full professional report (may take longer)' }
];

// Writing Style Section Component
function WritingStyleSection() {
  const [writingStyle, setWritingStyle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [reportContent, setReportContent] = useState('');
  const [reportType, setReportType] = useState('initial_assessment');
  const [expanded, setExpanded] = useState(false);
  const fileInputRef = useRef(null);

  const REPORT_TYPE_OPTIONS = [
    { value: 'initial_assessment', label: 'Initial Assessment' },
    { value: 'reassessment', label: 'Reassessment' },
    { value: 'progress', label: 'Progress / Update' },
    { value: 'discharge', label: 'Discharge' },
    { value: 'other', label: 'Other' },
  ];

  const typeLabel = (val) =>
    REPORT_TYPE_OPTIONS.find((o) => o.value === val)?.label || 'Other';

  useEffect(() => {
    fetchWritingStyle();
  }, []);

  const fetchWritingStyle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/therapist/writing-style`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWritingStyle(response.data);
    } catch (error) {
      console.error('Failed to fetch writing style:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!reportContent || reportContent.trim().length < 100) {
      toast.error('Please paste a report with at least 100 characters');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/therapist/writing-style/upload`,
        {
          title: reportTitle || `Example Report ${new Date().toLocaleDateString()}`,
          report_type: reportType,
          content: reportContent
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success('Report uploaded! AI will analyze your writing style.');
      setShowUploadDialog(false);
      setReportTitle('');
      setReportContent('');
      setReportType('initial_assessment');
      fetchWritingStyle();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to upload report';
      toast.error(String(msg));
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected later
    e.target.value = '';

    const allowed = ['.pdf', '.docx', '.txt'];
    const lower = (file.name || '').toLowerCase();
    if (!allowed.some((ext) => lower.endsWith(ext))) {
      toast.error('Please upload a PDF, DOCX, or TXT file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10 MB)');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', reportTitle || file.name);
      formData.append('report_type', reportType);

      await axios.post(
        `${API}/therapist/writing-style/upload-file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Report uploaded! Text extracted and saved.');
      setShowUploadDialog(false);
      setReportTitle('');
      setReportContent('');
      setReportType('initial_assessment');
      fetchWritingStyle();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to upload file';
      toast.error(String(msg));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this example report?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/therapist/writing-style/report/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report deleted');
      fetchWritingStyle();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  if (loading) return null;

  const exampleReports = writingStyle?.example_reports || [];
  const styleSummary = writingStyle?.style_summary;

  return (
    <Card className="p-5 bg-white rounded-2xl border border-stone-100" data-testid="writing-style-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-base font-medium text-primary-900">Report Writing Style</h3>
            <p className="text-xs text-stone-500">
              {exampleReports.length > 0 
                ? `${exampleReports.length} example report${exampleReports.length > 1 ? 's' : ''} • AI will match your style`
                : 'Teach AI your writing style by uploading example reports'}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-stone-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-stone-400" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-4"
          >
            <div className="bg-indigo-50 rounded-xl p-3">
              <p className="text-xs text-indigo-800">
                <strong>How it works:</strong> Upload 2+ example reports you've written. The AI will analyze your writing style 
                (tone, sentence structure, terminology) and match it when generating future reports.
              </p>
            </div>

            {/* Style Summary */}
            {styleSummary && (
              <div className="bg-stone-50 rounded-xl p-3">
                <h4 className="text-xs font-medium text-stone-700 mb-1 flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-primary" />
                  Your Writing Style (AI Analysis)
                </h4>
                <p className="text-xs text-stone-600 whitespace-pre-wrap">{styleSummary}</p>
              </div>
            )}

            {/* Example Reports List */}
            {exampleReports.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-stone-700">Uploaded Example Reports</h4>
                {exampleReports.map((report) => (
                  <div key={report.id} className="flex items-center justify-between bg-stone-50 rounded-lg p-2" data-testid={`example-report-${report.id}`}>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-stone-800 truncate">{report.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-medium">
                          {typeLabel(report.report_type)}
                        </span>
                        {report.source === 'file' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-medium">
                            File
                          </span>
                        )}
                        <p className="text-xs text-stone-500">
                          {new Date(report.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(report.id);
                      }}
                      className="text-red-500 hover:bg-red-50 h-7 text-xs"
                      data-testid={`delete-example-report-${report.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button */}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setShowUploadDialog(true);
              }}
              className="w-full rounded-xl"
              variant={exampleReports.length >= 2 ? "outline" : "default"}
              size="sm"
            >
              <FileText className="w-4 h-4 mr-2" />
              {exampleReports.length === 0 
                ? 'Upload Your First Example Report'
                : exampleReports.length === 1
                  ? 'Upload Another Report (1 more needed)'
                  : 'Upload Additional Example'}
            </Button>

            {exampleReports.length < 2 && (
              <p className="text-xs text-stone-500 text-center">
                Upload at least 2 reports for best style matching
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Example Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="report-title">Report Title (optional)</Label>
                <Input
                  id="report-title"
                  value={reportTitle}
                  onChange={(e) => setReportTitle(e.target.value)}
                  placeholder="e.g., Initial Assessment - May 2024"
                  className="rounded-lg"
                  data-testid="example-report-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report-type">Report Type</Label>
                <select
                  id="report-type"
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white"
                  data-testid="example-report-type-select"
                >
                  {REPORT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-indigo-900">Upload a PDF, DOCX, or TXT file</p>
                <p className="text-[11px] text-indigo-700">We&apos;ll automatically extract the text.</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="example-report-file-input"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                size="sm"
                variant="outline"
                className="rounded-lg"
                data-testid="example-report-file-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading…' : 'Choose File'}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-2 text-[11px] text-stone-500">or paste text</span></div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="report-content">
                Report Content <span className="text-stone-400">(optional if a file is uploaded above)</span>
              </Label>
              <Textarea
                id="report-content"
                value={reportContent}
                onChange={(e) => setReportContent(e.target.value)}
                placeholder="Paste your example report here... (minimum 100 characters)"
                className="h-48 resize-none"
                data-testid="example-report-content-textarea"
              />
              <p className="text-xs text-stone-500">
                {reportContent.length} characters {reportContent.length > 0 && reportContent.length < 100 && '(minimum 100)'}
              </p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Privacy Note:</strong> Example reports should not contain real client names or identifying information. 
                Replace names with placeholders like "[Child]" if needed.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} data-testid="example-report-cancel">
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || reportContent.length < 100}
              data-testid="example-report-submit-text"
            >
              {uploading ? 'Uploading...' : 'Upload Pasted Text'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Letterhead Section Component
function LetterheadSection() {
  const [letterhead, setLetterhead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [details, setDetails] = useState({
    practice_name: '',
    practice_address: '',
    practice_phone: '',
    practice_email: '',
    registration_number: ''
  });
  const fileInputRef = useState(null);

  useEffect(() => {
    fetchLetterhead();
  }, []);

  const fetchLetterhead = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/therapist/letterhead`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLetterhead(response.data);
      setDetails({
        practice_name: response.data.practice_name || '',
        practice_address: response.data.practice_address || '',
        practice_phone: response.data.practice_phone || '',
        practice_email: response.data.practice_email || '',
        registration_number: response.data.registration_number || ''
      });
    } catch (error) {
      console.error('Failed to fetch letterhead:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPEG, or GIF)');
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error('Image too large. Maximum size is 500KB.');
      return;
    }

    setUploading(true);
    
    // Convert to base64
    const reader = new FileReader();
    reader.onerror = () => {
      toast.error('Failed to read file');
      setUploading(false);
    };
    reader.onload = async (event) => {
      try {
        const base64 = event.target?.result;
        
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/therapist/letterhead/upload-image`,
          { image_data: base64 },
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success('Letterhead uploaded successfully!');
        fetchLetterhead();
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload letterhead');
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteImage = async () => {
    if (!window.confirm('Delete your letterhead image?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/therapist/letterhead/image`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Letterhead deleted');
      fetchLetterhead();
    } catch (error) {
      toast.error('Failed to delete letterhead');
    }
  };

  const handleSaveDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/therapist/letterhead/details`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: details
        }
      );
      toast.success('Practice details saved!');
      setShowDetailsDialog(false);
      fetchLetterhead();
    } catch (error) {
      toast.error('Failed to save details');
    }
  };

  if (loading) return null;

  return (
    <Card className="p-5 bg-white rounded-2xl border border-stone-100" data-testid="letterhead-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <Printer className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-base font-medium text-primary-900">Report Letterhead</h3>
            <p className="text-xs text-stone-500">
              {letterhead?.has_letterhead 
                ? 'Letterhead configured for exports'
                : 'Add your practice letterhead for report exports'}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-stone-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-stone-400" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-4"
          >
            <div className="bg-emerald-50 rounded-xl p-3">
              <p className="text-xs text-emerald-800">
                Upload your practice letterhead image. This will appear at the top of exported reports 
                when you copy them for Word or print.
              </p>
            </div>

            {/* Current Letterhead Preview */}
            {letterhead?.letterhead_image && (
              <div className="space-y-2">
                <Label className="text-xs font-medium text-stone-700">Current Letterhead</Label>
                <div className="relative border rounded-lg p-2 bg-white">
                  <img 
                    src={letterhead.letterhead_image} 
                    alt="Letterhead preview" 
                    className="max-h-32 w-auto mx-auto"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage();
                    }}
                    className="absolute top-1 right-1 text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Practice Details Summary */}
            {(letterhead?.practice_name || letterhead?.practice_address) && (
              <div className="bg-stone-50 rounded-lg p-3 space-y-1">
                {letterhead.practice_name && (
                  <p className="text-sm font-medium text-stone-800">{letterhead.practice_name}</p>
                )}
                {letterhead.practice_address && (
                  <p className="text-xs text-stone-600">{letterhead.practice_address}</p>
                )}
                {(letterhead.practice_phone || letterhead.practice_email) && (
                  <p className="text-xs text-stone-600">
                    {[letterhead.practice_phone, letterhead.practice_email].filter(Boolean).join(' • ')}
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="letterhead-upload"
                />
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById('letterhead-upload')?.click();
                  }}
                  variant={letterhead?.has_letterhead ? "outline" : "default"}
                  size="sm"
                  className="w-full rounded-xl"
                  disabled={uploading}
                >
                  {uploading ? (
                    'Uploading...'
                  ) : letterhead?.has_letterhead ? (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Replace Image
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Upload Letterhead
                    </>
                  )}
                </Button>
              </div>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetailsDialog(true);
                }}
                variant="outline"
                size="sm"
                className="rounded-xl"
              >
                <Edit className="w-4 h-4 mr-2" />
                Practice Details
              </Button>
            </div>

            <p className="text-xs text-stone-500 text-center">
              Supported formats: PNG, JPEG, GIF (max 500KB)
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Practice Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Practice Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="practice-name">Practice / Clinic Name</Label>
              <Input
                id="practice-name"
                value={details.practice_name}
                onChange={(e) => setDetails(prev => ({ ...prev, practice_name: e.target.value }))}
                placeholder="e.g., ABC Speech Therapy"
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="practice-address">Address</Label>
              <Textarea
                id="practice-address"
                value={details.practice_address}
                onChange={(e) => setDetails(prev => ({ ...prev, practice_address: e.target.value }))}
                placeholder="123 Main St, Suite 100&#10;City, State 12345"
                className="h-20 resize-none rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="practice-phone">Phone</Label>
                <Input
                  id="practice-phone"
                  value={details.practice_phone}
                  onChange={(e) => setDetails(prev => ({ ...prev, practice_phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="practice-email">Email</Label>
                <Input
                  id="practice-email"
                  value={details.practice_email}
                  onChange={(e) => setDetails(prev => ({ ...prev, practice_email: e.target.value }))}
                  placeholder="contact@practice.com"
                  className="rounded-lg"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration">Registration Number</Label>
              <Input
                id="registration"
                value={details.registration_number}
                onChange={(e) => setDetails(prev => ({ ...prev, registration_number: e.target.value }))}
                placeholder="e.g., HPCSA: SLT 0012345"
                className="rounded-lg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDetails}>
              Save Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Progress Reports Tab Component
function ProgressReportsTab({ childId, child, letterhead }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState('');
  const [reportType, setReportType] = useState('progress_summary');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [savedReports, setSavedReports] = useState([]);
  const [showReportDialog, setShowReportDialog] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, [childId]);

  const fetchReportData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [dataRes, reportsRes] = await Promise.all([
        axios.get(`${API}/reports/child-data/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      setReportData(dataRes.data);
      setSavedReports(reportsRes.data || []);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/reports/generate-ai`, {
        child_id: childId,
        report_type: reportType,
        additional_notes: additionalNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGeneratedReport(response.data.report || response.data.content || '');
      setShowReportDialog(true);
      toast.success('Report generated!');
      fetchReportData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const copyReport = async () => {
    if (!generatedReport) return;
    
    try {
      // Build letterhead for plain text
      let letterheadText = '';
      if (letterhead?.practice_name) {
        letterheadText += letterhead.practice_name + '\n';
        if (letterhead?.practice_address) letterheadText += letterhead.practice_address + '\n';
        const contact = [letterhead?.practice_phone, letterhead?.practice_email].filter(Boolean).join(' | ');
        if (contact) letterheadText += contact + '\n';
        if (letterhead?.registration_number) letterheadText += letterhead.registration_number + '\n';
        letterheadText += '\n' + '─'.repeat(50) + '\n\n';
      }
      
      const plainText = letterheadText + generatedReport;
      
      // Build HTML for Word
      let letterheadHTML = '';
      if (letterhead?.practice_name) {
        letterheadHTML = '<div style="text-align: center; margin-bottom: 24pt; border-bottom: 2px solid #333; padding-bottom: 16pt;">';
        if (letterhead?.letterhead_image) {
          letterheadHTML += `<img src="${letterhead.letterhead_image}" alt="Letterhead" style="max-height: 80px; margin-bottom: 8pt;" />`;
        }
        letterheadHTML += `<h2 style="margin: 0; font-size: 14pt;">${letterhead.practice_name}</h2>`;
        if (letterhead?.practice_address) {
          letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${letterhead.practice_address.replace(/\n/g, '<br>')}</p>`;
        }
        const contact = [letterhead?.practice_phone, letterhead?.practice_email].filter(Boolean).join(' | ');
        if (contact) letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${contact}</p>`;
        if (letterhead?.registration_number) {
          letterheadHTML += `<p style="margin: 4pt 0; font-size: 9pt; color: #777;">${letterhead.registration_number}</p>`;
        }
        letterheadHTML += '</div>';
      }
      
      const htmlReport = `<html><body style="font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.6;">${letterheadHTML}<div style="white-space: pre-wrap;">${generatedReport.replace(/\n/g, '<br>')}</div></body></html>`;
      
      if (navigator.clipboard.write) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([htmlReport], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' })
            })
          ]);
          toast.success('Report copied with letterhead!');
        } catch {
          await navigator.clipboard.writeText(plainText);
          toast.success('Report copied!');
        }
      } else {
        await navigator.clipboard.writeText(plainText);
        toast.success('Report copied!');
      }
    } catch {
      toast.error('Failed to copy');
    }
  };

  const printReport = () => {
    if (!generatedReport) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }
    
    let letterheadHTML = '';
    if (letterhead?.practice_name) {
      letterheadHTML = '<div style="text-align: center; margin-bottom: 24pt; border-bottom: 2px solid #333; padding-bottom: 16pt;">';
      if (letterhead?.letterhead_image) {
        letterheadHTML += `<img src="${letterhead.letterhead_image}" alt="Letterhead" style="max-height: 80px; margin-bottom: 8pt;" />`;
      }
      letterheadHTML += `<h2 style="margin: 0; font-size: 14pt;">${letterhead.practice_name}</h2>`;
      if (letterhead?.practice_address) {
        letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${letterhead.practice_address.replace(/\n/g, '<br>')}</p>`;
      }
      const contact = [letterhead?.practice_phone, letterhead?.practice_email].filter(Boolean).join(' | ');
      if (contact) letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${contact}</p>`;
      letterheadHTML += '</div>';
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Progress Report - ${child?.name}</title>
        <style>body { font-family: 'Segoe UI', sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
        h1 { color: #4a6355; border-bottom: 2px solid #e5e5e5; padding-bottom: 10px; }
        .meta { color: #666; font-size: 0.9rem; margin-bottom: 20px; }
        .content { white-space: pre-wrap; }</style>
      </head><body>${letterheadHTML}
        <h1>Progress Report - ${child?.name}</h1>
        <div class="meta">Generated: ${new Date().toLocaleDateString()}</div>
        <div class="content">${generatedReport}</div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-100">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-rose-600 mt-0.5" />
          <div>
            <p className="text-sm text-rose-900 font-medium">AI Progress Reports</p>
            <p className="text-xs text-rose-700 mt-1">
              Generate comprehensive progress reports from your SOAP notes and session data. 
              The AI synthesizes all documentation to create professional clinical reports.
            </p>
          </div>
        </div>
      </Card>

      {/* AI Disclaimer */}
      <Card className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-amber-900 font-medium">Please Review AI-Generated Content</p>
            <p className="text-xs text-amber-700 mt-1">
              AI-generated reports are designed to assist, not replace, clinical judgment. 
              While trained on neurodiversity-affirming language, <strong>errors may occur</strong>. 
              Please carefully review all content before finalizing reports.
            </p>
          </div>
        </div>
      </Card>

      {/* Data Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 text-center rounded-xl">
          <p className="text-2xl font-bold text-primary">{reportData?.chunks?.length || 0}</p>
          <p className="text-xs text-stone-500">Language Samples</p>
        </Card>
        <Card className="p-4 text-center rounded-xl">
          <p className="text-2xl font-bold text-primary">{reportData?.progress?.length || 0}</p>
          <p className="text-xs text-stone-500">SOAP Notes</p>
        </Card>
        <Card className="p-4 text-center rounded-xl">
          <p className="text-2xl font-bold text-primary">{reportData?.weekly_focuses?.length || 0}</p>
          <p className="text-xs text-stone-500">Weekly Focuses</p>
        </Card>
        <Card className="p-4 text-center rounded-xl">
          <p className="text-2xl font-bold text-primary">{savedReports.length}</p>
          <p className="text-xs text-stone-500">Saved Reports</p>
        </Card>
      </div>

      {/* Generate Report Form */}
      <Card className="p-6 rounded-2xl">
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          Generate New Report
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-stone-700 mb-2 block">Report Type</label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="progress_summary">Progress Summary (from SOAP notes)</SelectItem>
                <SelectItem value="clinical_report">Clinical Report (detailed)</SelectItem>
                <SelectItem value="parent_summary">Parent Summary (family-friendly)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-stone-700 mb-2 block">Additional Notes (optional)</label>
            <textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              className="w-full px-4 py-3 border rounded-xl text-sm min-h-[80px] resize-none"
              placeholder="Any specific focus areas or notes for the AI..."
            />
          </div>
          
          <Button 
            onClick={generateReport} 
            disabled={generating}
            className="w-full rounded-xl"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Report
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Saved Reports */}
      {savedReports.length > 0 && (
        <Card className="p-6 rounded-2xl">
          <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-stone-500" />
            Previous Reports ({savedReports.length})
          </h3>
          <div className="space-y-3">
            {savedReports.slice(0, 5).map((report) => (
              <div 
                key={report.id} 
                className="p-3 bg-stone-50 rounded-xl flex items-center justify-between cursor-pointer hover:bg-stone-100"
                onClick={() => {
                  setGeneratedReport(report.content || report.report || '');
                  setShowReportDialog(true);
                }}
              >
                <div>
                  <p className="text-sm font-medium">{report.report_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                  <p className="text-xs text-stone-500">{new Date(report.created_at).toLocaleDateString()}</p>
                </div>
                <Eye className="w-4 h-4 text-stone-400" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Generated Report</span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={copyReport} className="rounded-full">
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={printReport} className="rounded-full">
                  <Printer className="w-4 h-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm max-w-none mt-4 whitespace-pre-wrap">
            {generatedReport}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Assessment Tracker Tab Component
function AssessmentTrackerTab({ childId, availableTests, onCreateAssessment }) {
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTracker, setSelectedTracker] = useState(null);
  const [showAddTest, setShowAddTest] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [creatingTracker, setCreatingTracker] = useState(false);
  const [newTrackerTitle, setNewTrackerTitle] = useState('');
  
  // Local state for text inputs to prevent API calls on every keystroke
  const [localTestInputs, setLocalTestInputs] = useState({});
  
  // New test form state
  const [newTest, setNewTest] = useState({
    test_code: '',
    test_name: '',
    clinical_area: '',
    subtests: ''
  });
  
  // New session form state
  const [newSession, setNewSession] = useState({
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // PROMPT-hierarchy panel local state — mirrors the fields on the
  // Assessment doc so a tracker-driven workflow captures the same motor-
  // speech observations a standalone assessment would. Debounced save.
  const [promptDraft, setPromptDraft] = useState({
    prompt_jaw: '',
    prompt_lip: '',
    prompt_facial: '',
    prompt_lingual: '',
    prompt_sequencing: '',
    prompt_level: '',
    prompt_cueing: '',
    prompt_observations: '',
  });
  const [promptSaving, setPromptSaving] = useState(false);
  const promptSaveTimer = useRef(null);

  // Background & Clinical panel — Feb 2026 parity with Create Assessment.
  // Captures the same case-history + clinical-reasoning fields so a
  // tracker-driven workflow never omits anything a from-scratch flow has.
  const [bgDraft, setBgDraft] = useState({
    language_learner_profile: '',
    language_learner_profile_other: '',
    reason_for_referral: '',
    background_info: '',
    medical_history: '',
    developmental_history: '',
    language_sample_analysis: '',
    include_chunk_bank_data: false,
    clinical_impressions: '',
    diagnosis: '',
    recommendations: [],
    goals: [],
  });
  const [bgSaving, setBgSaving] = useState(false);
  const [chunkPulling, setChunkPulling] = useState(false);
  const bgSaveTimer = useRef(null);

  // Sync local draft whenever the selected tracker changes.
  useEffect(() => {
    if (!selectedTracker) return;
    setPromptDraft({
      prompt_jaw: selectedTracker.prompt_jaw || '',
      prompt_lip: selectedTracker.prompt_lip || '',
      prompt_facial: selectedTracker.prompt_facial || '',
      prompt_lingual: selectedTracker.prompt_lingual || '',
      prompt_sequencing: selectedTracker.prompt_sequencing || '',
      prompt_level: selectedTracker.prompt_level || '',
      prompt_cueing: selectedTracker.prompt_cueing || '',
      prompt_observations: selectedTracker.prompt_observations || '',
    });
    setBgDraft({
      language_learner_profile: selectedTracker.language_learner_profile || '',
      language_learner_profile_other: selectedTracker.language_learner_profile_other || '',
      reason_for_referral: selectedTracker.reason_for_referral || '',
      background_info: selectedTracker.background_info || '',
      medical_history: selectedTracker.medical_history || '',
      developmental_history: selectedTracker.developmental_history || '',
      language_sample_analysis: selectedTracker.language_sample_analysis || '',
      include_chunk_bank_data: !!selectedTracker.include_chunk_bank_data,
      clinical_impressions: selectedTracker.clinical_impressions || '',
      diagnosis: selectedTracker.diagnosis || '',
      recommendations: selectedTracker.recommendations || [],
      goals: selectedTracker.goals || [],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTracker?.id]);

  const saveBackground = async (patch) => {
    if (!selectedTracker) return;
    setBgSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/assessment-tracker/${selectedTracker.id}/background`,
        patch,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      toast.error('Failed to save background');
    } finally {
      setBgSaving(false);
    }
  };

  const onBgChange = (field, value, immediate = false) => {
    setBgDraft((prev) => ({ ...prev, [field]: value }));
    if (bgSaveTimer.current) clearTimeout(bgSaveTimer.current);
    if (immediate) {
      saveBackground({ [field]: value });
    } else {
      // Debounce free-text fields
      bgSaveTimer.current = setTimeout(() => saveBackground({ [field]: value }), 800);
    }
  };

  const pullChunkBank = async () => {
    if (!selectedTracker) return;
    setChunkPulling(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/assessment-tracker/${selectedTracker.id}/pull-chunk-bank`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBgDraft((prev) => ({
        ...prev,
        language_sample_analysis: r.data?.language_sample_analysis || prev.language_sample_analysis,
        include_chunk_bank_data: true,
      }));
      toast.success(`Pulled ${r.data?.chunk_count ?? 0} chunk${r.data?.chunk_count === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Chunk-bank pull failed');
    } finally {
      setChunkPulling(false);
    }
  };

  const savePromptHierarchy = async (patch) => {
    if (!selectedTracker) return;
    setPromptSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/assessment-tracker/${selectedTracker.id}/prompt-hierarchy`,
        patch,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      toast.error('Failed to save PROMPT hierarchy');
    } finally {
      setPromptSaving(false);
    }
  };

  const onPromptChange = (field, value) => {
    setPromptDraft((prev) => ({ ...prev, [field]: value }));
    // Dropdowns save immediately; text field debounces 600ms.
    if (promptSaveTimer.current) clearTimeout(promptSaveTimer.current);
    if (field === 'prompt_observations') {
      promptSaveTimer.current = setTimeout(() => {
        savePromptHierarchy({ [field]: value });
      }, 600);
    } else {
      savePromptHierarchy({ [field]: value });
    }
  };

  // Clinical areas for selection
  const CLINICAL_AREAS = [
    { id: 'prelinguistic', name: 'Prelinguistic Skills' },
    { id: 'receptive', name: 'Receptive Language' },
    { id: 'expressive', name: 'Expressive Language' },
    { id: 'auditory', name: 'Auditory Processing' },
    { id: 'literacy', name: 'Literacy' },
    { id: 'social_emotional', name: 'Social-Emotional Skills' },
    { id: 'speech_motor', name: 'Speech & Oral Motor' },
    { id: 'aac', name: 'AAC' },
    { id: 'feeding', name: 'Feeding & Swallowing' }
  ];

  useEffect(() => {
    fetchTrackers();
  }, [childId]);

  const fetchTrackers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessment-tracker/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrackers(response.data);
      // Auto-select the first active tracker if available
      const activeTracker = response.data.find(t => t.status === 'active');
      if (activeTracker && !selectedTracker) {
        setSelectedTracker(activeTracker);
      }
    } catch (error) {
      console.error('Failed to fetch trackers:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTracker = async () => {
    setCreatingTracker(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/assessment-tracker/${childId}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { title: newTrackerTitle || 'Assessment Tracker' }
        }
      );
      toast.success('Tracker created!');
      setNewTrackerTitle('');
      fetchTrackers();
      setSelectedTracker(response.data.tracker);
    } catch (error) {
      toast.error('Failed to create tracker');
    } finally {
      setCreatingTracker(false);
    }
  };

  const addPlannedTest = async () => {
    if (!selectedTracker || !newTest.test_code) {
      toast.error('Please select a test');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const testName = availableTests[newTest.test_code]?.name || newTest.test_code;
      
      await axios.post(
        `${API}/assessment-tracker/${selectedTracker.id}/plan-test`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            test_code: newTest.test_code,
            test_name: testName,
            clinical_area: newTest.clinical_area,
            subtests: newTest.subtests
          }
        }
      );
      
      toast.success('Test added to tracker!');
      setShowAddTest(false);
      setNewTest({ test_code: '', test_name: '', clinical_area: '', subtests: '' });
      refreshTracker();
    } catch (error) {
      toast.error('Failed to add test');
    }
  };

  const updateTestStatus = async (testId, status, notes = null, errorsObserved = null, scores = null, skipRefresh = false) => {
    if (!selectedTracker) return;

    try {
      const token = localStorage.getItem('token');
      const params = { status };
      if (notes !== null) params.notes = notes;
      if (errorsObserved !== null) params.errors_observed = errorsObserved;
      if (scores !== null) params.scores = JSON.stringify(scores);

      await axios.put(
        `${API}/assessment-tracker/${selectedTracker.id}/test/${testId}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );
      
      // Only refresh if not a debounced text/score update
      if (!skipRefresh) {
        refreshTracker();
      }
    } catch (error) {
      toast.error('Failed to update test');
    }
  };

  // Update a single score in a test's scores array
  const updateTestScore = (testId, subtestName, field, value) => {
    if (!selectedTracker) return;
    
    const test = selectedTracker.planned_tests?.find(t => t.id === testId);
    if (!test) return;
    
    // Get current scores or initialize
    const currentScores = test.scores || [];
    
    // Find or create score entry for this subtest
    let scoreIndex = currentScores.findIndex(s => s.subtest_name === subtestName);
    let newScores;
    
    if (scoreIndex === -1) {
      // Create new score entry
      newScores = [...currentScores, { subtest_name: subtestName, score_type: 'standard', [field]: value }];
    } else {
      // Update existing score entry
      newScores = currentScores.map((s, i) => 
        i === scoreIndex ? { ...s, [field]: value } : s
      );
    }
    
    // Update via API
    updateTestStatus(testId, test.status, null, null, newScores);
  };

  // Helper to get local input value or fall back to server value
  const getLocalInputValue = (testId, field, serverValue) => {
    const key = `${testId}-${field}`;
    return localTestInputs[key] !== undefined ? localTestInputs[key] : (serverValue || '');
  };

  // Handle local text input change - just update local state, save on blur
  const handleTextInputChange = (testId, field, value) => {
    const key = `${testId}-${field}`;
    setLocalTestInputs(prev => ({ ...prev, [key]: value }));
  };

  // Save text input on blur
  const handleTextInputBlur = async (testId, field, value, status) => {
    const key = `${testId}-${field}`;
    
    // Only save if there's a local value (user actually typed something)
    if (localTestInputs[key] !== undefined) {
      if (field === 'notes') {
        await updateTestStatus(testId, status, value, null, null, true);
      } else if (field === 'errors_observed') {
        await updateTestStatus(testId, status, null, value, null, true);
      }
    }
  };

  // Handle score input change - just update local state
  const handleScoreInputChange = (testId, subtestName, field, value) => {
    const key = `${testId}-${subtestName}-${field}`;
    setLocalTestInputs(prev => ({ ...prev, [key]: value }));
  };

  // Save score input on blur.
  //
  // Critical: NEVER rely on `currentScores` passed from render — it can
  // be a stale snapshot when the user types in several score fields in
  // quick succession (we skip the refresh between blurs to keep typing
  // smooth). Instead, read the freshest test.scores directly from the
  // React state via a functional ref lookup. Previously, back-to-back
  // blurs on different fields could clobber each other's writes because
  // each blur built its new array from the same pre-typing snapshot.
  const handleScoreInputBlur = async (testId, subtestName, field, value, _currentScoresIgnored, status) => {
    const key = `${testId}-${subtestName}-${field}`;
    if (localTestInputs[key] === undefined) return;

    // Look up the freshest scores for this test from the latest tracker
    // state — this reflects every prior blur/save that has landed.
    setSelectedTracker((currentTracker) => {
      if (!currentTracker) return currentTracker;
      const test = currentTracker.planned_tests?.find((t) => t.id === testId);
      const scores = (test && test.scores) || [];
      const scoreIndex = scores.findIndex((s) => s.subtest_name === subtestName);

      let parsedValue = value;
      if (
        field === 'percentile_rank' ||
        field === 'converted_score' ||
        field === 'scaled_score' ||
        field === 'standard_score' ||
        field === 'raw_score'
      ) {
        // Empty string clears the field; anything else stored as a number.
        const trimmed = String(value ?? '').trim();
        parsedValue = trimmed === '' ? null : Number(trimmed);
        if (Number.isNaN(parsedValue)) parsedValue = null;
      }

      let newScores;
      if (scoreIndex === -1) {
        newScores = [
          ...scores,
          { subtest_name: subtestName, score_type: 'standard', [field]: parsedValue },
        ];
      } else {
        newScores = scores.map((s, i) =>
          i === scoreIndex ? { ...s, [field]: parsedValue } : s,
        );
      }

      // Optimistically patch the tracker state so the NEXT blur reads a
      // fresh snapshot — even before the server has confirmed.
      const patchedTests = currentTracker.planned_tests.map((t) =>
        t.id === testId ? { ...t, scores: newScores } : t,
      );

      // Fire-and-forget PUT with the FRESH array. skipRefresh keeps the
      // typing rhythm smooth; the optimistic patch above already gives
      // subsequent blurs the correct baseline.
      updateTestStatus(testId, status, null, null, newScores, true);

      return { ...currentTracker, planned_tests: patchedTests };
    });
  };

  // Get local score value or server value
  const getLocalScoreValue = (testId, subtestName, field, serverValue) => {
    const key = `${testId}-${subtestName}-${field}`;
    return localTestInputs[key] !== undefined ? localTestInputs[key] : (serverValue ?? '');
  };

  const removeTest = async (testId) => {
    if (!selectedTracker) return;
    if (!window.confirm('Remove this test from the tracker?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/assessment-tracker/${selectedTracker.id}/test/${testId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Test removed');
      refreshTracker();
    } catch (error) {
      toast.error('Failed to remove test');
    }
  };

  const addSession = async () => {
    if (!selectedTracker) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/assessment-tracker/${selectedTracker.id}/session`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            date: newSession.date,
            notes: newSession.notes
          }
        }
      );
      
      toast.success('Session added!');
      setShowAddSession(false);
      setNewSession({ date: new Date().toISOString().split('T')[0], notes: '' });
      refreshTracker();
    } catch (error) {
      toast.error('Failed to add session');
    }
  };

  const createAssessmentFromTracker = async () => {
    if (!selectedTracker) return;
    
    const completedTests = selectedTracker.planned_tests?.filter(t => t.status === 'completed') || [];
    if (completedTests.length === 0) {
      toast.error('Complete at least one test before creating an assessment');
      return;
    }

    if (!window.confirm('This will create an assessment from the completed tests in this tracker. Continue?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/assessment-tracker/${selectedTracker.id}/create-assessment`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { assessment_type: 'initial' }
        }
      );
      
      toast.success('Assessment created from tracker!');
      if (onCreateAssessment) {
        onCreateAssessment(response.data.assessment_id);
      }
    } catch (error) {
      toast.error('Failed to create assessment');
    }
  };

  // Push tracker changes into the EXISTING assessment linked to this
  // tracker. Solves the pain: after the first "Create Assessment", the
  // therapist could only re-create a duplicate, never update the one
  // she'd already generated. Uses the existing sync-from-tracker
  // endpoint on the backend.
  const syncToExistingAssessment = async () => {
    if (!selectedTracker?.assessment_id) return;
    if (!window.confirm(
      'Update the existing assessment with the latest scores from this tracker?\n\n' +
      'Existing narrative sections stay untouched; only the scores are refreshed.'
    )) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/assessments/${selectedTracker.assessment_id}/sync-from-tracker`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Assessment updated ✨');
      if (onCreateAssessment) {
        // Reuse the same open-assessment callback so the therapist
        // lands on the freshly-updated assessment.
        onCreateAssessment(selectedTracker.assessment_id);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update assessment');
    }
  };

  const deleteTracker = async (trackerId) => {
    if (!window.confirm('Delete this tracker? This cannot be undone.')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assessment-tracker/${trackerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tracker deleted');
      if (selectedTracker?.id === trackerId) {
        setSelectedTracker(null);
      }
      fetchTrackers();
    } catch (error) {
      toast.error('Failed to delete tracker');
    }
  };

  const refreshTracker = async () => {
    if (!selectedTracker) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessment-tracker/detail/${selectedTracker.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTracker(response.data);
      fetchTrackers(); // Also refresh the list
    } catch (error) {
      console.error('Failed to refresh tracker:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-amber-600" />;
      case 'skipped':
        return <X className="w-4 h-4 text-stone-400" />;
      default:
        return <Circle className="w-4 h-4 text-stone-300" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">In Progress</Badge>;
      case 'skipped':
        return <Badge className="bg-stone-100 text-stone-500 hover:bg-stone-100">Skipped</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Planned</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assessment-tracker-tab">
      {/* Info Banner */}
      <Card className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl border border-violet-100">
        <div className="flex items-start gap-3">
          <ListChecks className="w-5 h-5 text-violet-600 mt-0.5" />
          <div>
            <p className="text-sm text-violet-900 font-medium">Assessment Tracker</p>
            <p className="text-xs text-violet-700 mt-1">
              Plan and track assessment tests across multiple sessions. Check off completed tests, 
              add notes about errors observed, then convert to a full assessment when ready.
            </p>
          </div>
        </div>
      </Card>

      {/* Tracker Selection / Creation */}
      <div className="flex flex-wrap gap-3 items-center">
        {trackers.map(tracker => (
          <button
            key={tracker.id}
            onClick={() => setSelectedTracker(tracker)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              selectedTracker?.id === tracker.id
                ? 'bg-primary text-white shadow-md'
                : 'bg-white border border-stone-200 text-stone-700 hover:border-primary hover:text-primary'
            }`}
            data-testid={`tracker-btn-${tracker.id}`}
          >
            {tracker.title}
            {tracker.status === 'completed' && (
              <CheckCircle2 className="w-3 h-3 inline ml-1.5" />
            )}
          </button>
        ))}
        
        {/* Create New Tracker */}
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-xl" data-testid="create-tracker-btn">
              <Plus className="w-4 h-4 mr-1" /> New Tracker
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle>Create Assessment Tracker</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tracker Name</Label>
                <Input
                  value={newTrackerTitle}
                  onChange={(e) => setNewTrackerTitle(e.target.value)}
                  placeholder="e.g., Initial Assessment - January 2025"
                  className="rounded-lg"
                />
              </div>
              <p className="text-xs text-stone-500">
                Create a tracker to plan which tests to administer across multiple sessions. 
                You can check off tests as you complete them and add notes along the way.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={createTracker} disabled={creatingTracker} className="rounded-full">
                {creatingTracker ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Tracker
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Selected Tracker Details */}
      {selectedTracker ? (
        <Card className="p-5 bg-white rounded-2xl border border-stone-100">
          {/* Tracker Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-primary-900">{selectedTracker.title}</h3>
              <p className="text-xs text-stone-500 mt-1">
                Created {new Date(selectedTracker.created_at).toLocaleDateString()} • 
                {selectedTracker.planned_tests?.length || 0} tests planned • 
                {selectedTracker.sessions?.length || 0} sessions logged
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteTracker(selectedTracker.id)}
                className="text-red-500 hover:text-red-600 rounded-full"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Progress Summary */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            {['planned', 'in_progress', 'completed', 'skipped'].map(status => {
              const count = selectedTracker.planned_tests?.filter(t => t.status === status).length || 0;
              const total = selectedTracker.planned_tests?.length || 0;
              return (
                <div key={status} className={`p-3 rounded-xl text-center ${
                  status === 'completed' ? 'bg-green-50' :
                  status === 'in_progress' ? 'bg-amber-50' :
                  status === 'skipped' ? 'bg-stone-50' :
                  'bg-blue-50'
                }`}>
                  <p className="text-2xl font-bold text-primary-900">{count}</p>
                  <p className="text-xs text-stone-600 capitalize">{status.replace('_', ' ')}</p>
                </div>
              );
            })}
          </div>

          {/* Visual Progress Bar */}
          {selectedTracker.planned_tests?.length > 0 && (
            <div className="mb-6" data-testid="tracker-progress-bar">
              {(() => {
                const tests = selectedTracker.planned_tests || [];
                const total = tests.length;
                const completed = tests.filter(t => t.status === 'completed').length;
                const inProgress = tests.filter(t => t.status === 'in_progress').length;
                const skipped = tests.filter(t => t.status === 'skipped').length;
                const planned = tests.filter(t => t.status === 'planned').length;
                
                const completedPct = (completed / total) * 100;
                const inProgressPct = (inProgress / total) * 100;
                const skippedPct = (skipped / total) * 100;
                
                return (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-stone-600">Overall Progress</span>
                      <span className="text-sm font-bold text-primary-900">
                        {Math.round(completedPct)}% Complete
                      </span>
                    </div>
                    <div className="h-3 w-full bg-stone-100 rounded-full overflow-hidden flex">
                      {completedPct > 0 && (
                        <div 
                          className="h-full bg-green-500 transition-all duration-300" 
                          style={{ width: `${completedPct}%` }}
                        />
                      )}
                      {inProgressPct > 0 && (
                        <div 
                          className="h-full bg-amber-400 transition-all duration-300" 
                          style={{ width: `${inProgressPct}%` }}
                        />
                      )}
                      {skippedPct > 0 && (
                        <div 
                          className="h-full bg-stone-300 transition-all duration-300" 
                          style={{ width: `${skippedPct}%` }}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Completed ({completed})
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                        In Progress ({inProgress})
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-stone-300 rounded-full"></span>
                        Skipped ({skipped})
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                        Planned ({planned})
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Planned Tests List */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-stone-700">Planned Tests</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddTest(true)}
                className="rounded-full"
                data-testid="add-test-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Test
              </Button>
            </div>

            {selectedTracker.planned_tests?.length === 0 ? (
              <div className="text-center py-8 bg-stone-50 rounded-xl">
                <ClipboardCheck className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                <p className="text-sm text-stone-500">No tests planned yet</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddTest(true)}
                  className="mt-3 rounded-full"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Your First Test
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedTracker.planned_tests.map((test) => (
                  <div
                    key={test.id}
                    className={`p-4 rounded-xl border transition-all ${
                      test.status === 'completed' ? 'bg-green-50 border-green-200' :
                      test.status === 'in_progress' ? 'bg-amber-50 border-amber-200' :
                      test.status === 'skipped' ? 'bg-stone-50 border-stone-200' :
                      'bg-white border-stone-200'
                    }`}
                    data-testid={`test-item-${test.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => {
                            const nextStatus = 
                              test.status === 'planned' ? 'in_progress' :
                              test.status === 'in_progress' ? 'completed' :
                              test.status === 'completed' ? 'planned' :
                              'planned';
                            updateTestStatus(test.id, nextStatus);
                          }}
                          className="mt-0.5"
                        >
                          {getStatusIcon(test.status)}
                        </button>
                        <div>
                          <p className="font-medium text-primary-900">
                            {test.test_name || test.test_code}
                          </p>
                          {test.subtests?.length > 0 && (
                            <p className="text-xs text-stone-500 mt-0.5">
                              Subtests: {test.subtests.join(', ')}
                            </p>
                          )}
                          {test.clinical_area && (
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {CLINICAL_AREAS.find(a => a.id === test.clinical_area)?.name || test.clinical_area}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(test.status)}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTest(test.id)}
                          className="h-7 w-7 p-0 text-stone-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Notes & Errors for the test */}
                    {(test.status === 'in_progress' || test.status === 'completed') && (
                      <div className="mt-3 pt-3 border-t border-stone-200 space-y-4">
                        {/* Scores Entry Section */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-medium text-stone-700 flex items-center gap-1">
                              <FileText className="w-3 h-3" /> Test Scores
                            </Label>
                            <span className="text-xs text-stone-400">
                              {test.scores?.length || 0} scores entered
                            </span>
                          </div>
                          
                          {/* Score entries for each subtest */}
                          {(test.subtests?.length > 0 ? test.subtests : ['Total']).map((subtest, idx) => {
                            const existingScore = test.scores?.find(s => s.subtest_name === subtest) || {};
                            return (
                              <div key={idx} className="p-3 bg-white rounded-lg border border-stone-100">
                                <p className="text-xs font-medium text-stone-600 mb-2">{subtest}</p>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs text-stone-500">Score Type</Label>
                                    <Select
                                      value={existingScore.score_type || 'standard'}
                                      onValueChange={(v) => updateTestScore(test.id, subtest, 'score_type', v)}
                                    >
                                      <SelectTrigger className="h-8 text-xs rounded-lg">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="standard">Standard (M=100)</SelectItem>
                                        <SelectItem value="scaled">Scaled (M=10)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-stone-500">
                                      {existingScore.score_type === 'scaled' ? 'Scaled Score' : 'Standard Score'}
                                    </Label>
                                    <Input
                                      type="number"
                                      value={getLocalScoreValue(
                                        test.id, 
                                        subtest, 
                                        existingScore.score_type === 'scaled' ? 'scaled_score' : 'converted_score',
                                        existingScore.score_type === 'scaled' 
                                          ? existingScore.scaled_score 
                                          : (existingScore.converted_score ?? existingScore.standard_score)
                                      )}
                                      onChange={(e) => {
                                        const field = existingScore.score_type === 'scaled' ? 'scaled_score' : 'converted_score';
                                        handleScoreInputChange(test.id, subtest, field, e.target.value);
                                      }}
                                      onBlur={(e) => {
                                        const field = existingScore.score_type === 'scaled' ? 'scaled_score' : 'converted_score';
                                        handleScoreInputBlur(test.id, subtest, field, e.target.value, test.scores, test.status);
                                      }}
                                      placeholder={existingScore.score_type === 'scaled' ? '1-19' : '40-160'}
                                      className="h-8 text-xs rounded-lg"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-stone-500">Percentile</Label>
                                    <Input
                                      type="number"
                                      value={getLocalScoreValue(test.id, subtest, 'percentile_rank', existingScore.percentile_rank)}
                                      onChange={(e) => handleScoreInputChange(test.id, subtest, 'percentile_rank', e.target.value)}
                                      onBlur={(e) => handleScoreInputBlur(test.id, subtest, 'percentile_rank', e.target.value, test.scores, test.status)}
                                      placeholder="1-99"
                                      className="h-8 text-xs rounded-lg"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-stone-500">Age Equiv.</Label>
                                    <Input
                                      type="text"
                                      value={getLocalScoreValue(test.id, subtest, 'age_equivalent', existingScore.age_equivalent)}
                                      onChange={(e) => handleScoreInputChange(test.id, subtest, 'age_equivalent', e.target.value)}
                                      onBlur={(e) => handleScoreInputBlur(test.id, subtest, 'age_equivalent', e.target.value, test.scores, test.status)}
                                      placeholder="e.g., 4;6"
                                      className="h-8 text-xs rounded-lg"
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Notes */}
                        <div className="space-y-1">
                          <Label className="text-xs text-stone-600">Notes</Label>
                          <Textarea
                            value={getLocalInputValue(test.id, 'notes', test.notes)}
                            onChange={(e) => handleTextInputChange(test.id, 'notes', e.target.value)}
                            onBlur={(e) => handleTextInputBlur(test.id, 'notes', e.target.value, test.status)}
                            placeholder="Session observations, child's engagement..."
                            className="min-h-16 text-sm rounded-lg bg-white"
                          />
                        </div>
                        
                        {/* Errors Observed */}
                        <div className="space-y-1">
                          <Label className="text-xs text-stone-600">Errors Observed</Label>
                          <Textarea
                            value={getLocalInputValue(test.id, 'errors_observed', test.errors_observed)}
                            onChange={(e) => handleTextInputChange(test.id, 'errors_observed', e.target.value)}
                            onBlur={(e) => handleTextInputBlur(test.id, 'errors_observed', e.target.value, test.status)}
                            placeholder="Specific error patterns, items missed..."
                            className="min-h-16 text-sm rounded-lg bg-white"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sessions Log */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-stone-700">Sessions Log</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddSession(true)}
                className="rounded-full"
                data-testid="add-session-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Log Session
              </Button>
            </div>

            {selectedTracker.sessions?.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-4">No sessions logged yet</p>
            ) : (
              <div className="space-y-2">
                {selectedTracker.sessions.map((session, idx) => (
                  <div key={idx} className="p-3 bg-stone-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">Session {session.session_number}</Badge>
                        <span className="text-xs text-stone-500">
                          {new Date(session.date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    {session.notes && (
                      <p className="text-sm text-stone-600 mt-2">{session.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Background & Clinical — Feb 2026 parity with Create Assessment.
              Same case-history + clinical-reasoning fields, so a tracker-
              driven workflow captures everything a from-scratch assessment
              would. Carries into the assessment on 'Create Assessment from
              Tracker' — no re-entry. */}
          <div className="pt-4 border-t border-stone-100" data-testid="tracker-background">
            <Accordion type="multiple" className="space-y-2">
              <AccordionItem value="tracker-background" className="border rounded-lg px-3 bg-white border-emerald-200">
                <AccordionTrigger className="hover:no-underline py-3">
                  <span className="text-sm font-medium text-emerald-900 flex items-center gap-2">
                    Background &amp; Clinical Info
                    {bgSaving && <Loader2 className="w-3 h-3 animate-spin text-emerald-500" />}
                    <span className="text-xs text-stone-500 font-normal">
                      · Same fields as Create Assessment
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pb-3">
                  <p className="text-xs text-emerald-800">
                    Everything here transfers verbatim into the assessment when
                    you press "Create Assessment from Tracker" — no re-typing.
                  </p>

                  <div>
                    <Label className="text-xs">Language Learner Profile</Label>
                    <Select
                      value={bgDraft.language_learner_profile}
                      onValueChange={(v) => onBgChange('language_learner_profile', v, true)}
                    >
                      <SelectTrigger className="h-10" data-testid="bg-language-learner-profile">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GLP">Gestalt Language Processor (GLP)</SelectItem>
                        <SelectItem value="Analytical">Analytical</SelectItem>
                        <SelectItem value="Mixed">Mixed (GLP + Analytical)</SelectItem>
                        <SelectItem value="AAC">AAC user</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {bgDraft.language_learner_profile === 'Other' && (
                      <Input
                        placeholder="Please specify"
                        value={bgDraft.language_learner_profile_other}
                        onChange={(e) => onBgChange('language_learner_profile_other', e.target.value)}
                        className="h-10 mt-2"
                        data-testid="bg-language-learner-other"
                      />
                    )}
                  </div>

                  <div>
                    <Label className="text-xs">Reason for Referral</Label>
                    <Textarea
                      value={bgDraft.reason_for_referral}
                      onChange={(e) => onBgChange('reason_for_referral', e.target.value)}
                      rows={2}
                      placeholder="Why is this child being assessed?"
                      className="text-sm"
                      data-testid="bg-reason-for-referral"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Background Information</Label>
                    <Textarea
                      value={bgDraft.background_info}
                      onChange={(e) => onBgChange('background_info', e.target.value)}
                      rows={3}
                      placeholder="Family, schooling, prior services, cultural/linguistic context…"
                      className="text-sm"
                      data-testid="bg-background-info"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Medical History</Label>
                      <Textarea
                        value={bgDraft.medical_history}
                        onChange={(e) => onBgChange('medical_history', e.target.value)}
                        rows={3}
                        placeholder="Relevant medical, hearing, vision, etc."
                        className="text-sm"
                        data-testid="bg-medical-history"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Developmental History</Label>
                      <Textarea
                        value={bgDraft.developmental_history}
                        onChange={(e) => onBgChange('developmental_history', e.target.value)}
                        rows={3}
                        placeholder="Milestones, delays, early red flags…"
                        className="text-sm"
                        data-testid="bg-developmental-history"
                      />
                    </div>
                  </div>

                  {/* Language sample + chunk-bank pull */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Language Sample Analysis</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={pullChunkBank}
                        disabled={chunkPulling}
                        className="h-7 text-xs rounded-full"
                        data-testid="bg-pull-chunk-bank"
                      >
                        {chunkPulling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                        Pull chunk bank
                      </Button>
                    </div>
                    <Textarea
                      value={bgDraft.language_sample_analysis}
                      onChange={(e) => onBgChange('language_sample_analysis', e.target.value)}
                      rows={5}
                      placeholder="Add utterance samples, or click 'Pull chunk bank' to snapshot this child's chunks for analysis."
                      className="text-sm font-mono"
                      data-testid="bg-language-sample-analysis"
                    />
                    <label className="flex items-center gap-2 mt-2 text-xs text-stone-600">
                      <input
                        type="checkbox"
                        checked={bgDraft.include_chunk_bank_data}
                        onChange={(e) => onBgChange('include_chunk_bank_data', e.target.checked, true)}
                        data-testid="bg-include-chunk-bank"
                      />
                      Include chunk-bank data in the generated assessment report
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Clinical Impressions</Label>
                      <Textarea
                        value={bgDraft.clinical_impressions}
                        onChange={(e) => onBgChange('clinical_impressions', e.target.value)}
                        rows={3}
                        placeholder="Your clinical formulation so far…"
                        className="text-sm"
                        data-testid="bg-clinical-impressions"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Working Diagnosis (optional)</Label>
                      <Input
                        value={bgDraft.diagnosis}
                        onChange={(e) => onBgChange('diagnosis', e.target.value)}
                        placeholder="e.g. DLD, CAS, expressive delay…"
                        className="text-sm h-10"
                        data-testid="bg-diagnosis"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* PROMPT Hierarchy — mirrors the standalone assessment's motor-
              speech panel so tracker-driven workflows capture the same data.
              Auto-transfers to the assessment when 'Create Assessment from
              Tracker' fires (and via 'Sync from tracker' on the report page). */}
          <div className="pt-4 border-t border-stone-100" data-testid="tracker-prompt-hierarchy">
            <Accordion type="multiple" className="space-y-2">
              <AccordionItem value="tracker-prompt" className="border rounded-lg px-3 bg-white border-purple-200">
                <AccordionTrigger className="hover:no-underline py-3">
                  <span className="text-sm font-medium text-purple-900 flex items-center gap-2">
                    PROMPT Hierarchy
                    {promptSaving && <Loader2 className="w-3 h-3 animate-spin text-purple-500" />}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pb-3">
                  <p className="text-xs text-purple-700">
                    Optional — for motor-speech (PROMPT) assessments. Anything you type here transfers
                    straight into the assessment report's PROMPT panel, so you never re-enter it.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Jaw (Mandibular) Control</Label>
                      <Select value={promptDraft.prompt_jaw} onValueChange={(v) => onPromptChange('prompt_jaw', v)}>
                        <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="tracker-prompt-jaw">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adequate">Adequate</SelectItem>
                          <SelectItem value="emerging">Emerging</SelectItem>
                          <SelectItem value="requires_support">Requires Support</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Lip Control</Label>
                      <Select value={promptDraft.prompt_lip} onValueChange={(v) => onPromptChange('prompt_lip', v)}>
                        <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="tracker-prompt-lip">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adequate">Adequate</SelectItem>
                          <SelectItem value="emerging">Emerging</SelectItem>
                          <SelectItem value="requires_support">Requires Support</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Facial Control</Label>
                      <Select value={promptDraft.prompt_facial} onValueChange={(v) => onPromptChange('prompt_facial', v)}>
                        <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="tracker-prompt-facial">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adequate">Adequate</SelectItem>
                          <SelectItem value="emerging">Emerging</SelectItem>
                          <SelectItem value="requires_support">Requires Support</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Lingual Control</Label>
                      <Select value={promptDraft.prompt_lingual} onValueChange={(v) => onPromptChange('prompt_lingual', v)}>
                        <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="tracker-prompt-lingual">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adequate">Adequate</SelectItem>
                          <SelectItem value="emerging">Emerging</SelectItem>
                          <SelectItem value="requires_support">Requires Support</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Sequencing (Sequenced Movements)</Label>
                      <Select value={promptDraft.prompt_sequencing} onValueChange={(v) => onPromptChange('prompt_sequencing', v)}>
                        <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="tracker-prompt-sequencing">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adequate">Adequate</SelectItem>
                          <SelectItem value="emerging">Emerging</SelectItem>
                          <SelectItem value="requires_support">Requires Support</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Hierarchy Level</Label>
                      <Select value={promptDraft.prompt_level} onValueChange={(v) => onPromptChange('prompt_level', v)}>
                        <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="tracker-prompt-level">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="surface">Surface</SelectItem>
                          <SelectItem value="complex">Complex</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label className="text-xs">Cueing Type Used</Label>
                      <Select value={promptDraft.prompt_cueing} onValueChange={(v) => onPromptChange('prompt_cueing', v)}>
                        <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="tracker-prompt-cueing">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tactile">Tactile</SelectItem>
                          <SelectItem value="auditory">Auditory</SelectItem>
                          <SelectItem value="visual">Visual</SelectItem>
                          <SelectItem value="multimodal">Multimodal (tactile + auditory + visual)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">PROMPT Observations</Label>
                    <Textarea
                      value={promptDraft.prompt_observations}
                      onChange={(e) => onPromptChange('prompt_observations', e.target.value)}
                      placeholder="Motor planning observations, tactile cueing response, tone/phonatory notes..."
                      className="min-h-20 rounded-lg text-sm"
                      data-testid="tracker-prompt-observations"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-stone-100">
            <Button
              onClick={createAssessmentFromTracker}
              className="rounded-full"
              disabled={!selectedTracker.planned_tests?.some(t => t.status === 'completed')}
              data-testid="create-assessment-from-tracker-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Create Assessment from Tracker
            </Button>
            {selectedTracker.assessment_id && (
              <>
                <Button
                  onClick={syncToExistingAssessment}
                  variant="outline"
                  className="rounded-full"
                  data-testid="sync-to-existing-assessment-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Update Existing Assessment
                </Button>
                <Badge className="bg-green-100 text-green-700 self-center">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Assessment Linked
                </Badge>
              </>
            )}
          </div>
        </Card>
      ) : (
        <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100">
          <ListChecks className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-700 mb-2">No tracker selected</h3>
          <p className="text-stone-500 mb-6">
            Create a new tracker or select an existing one to plan your assessment tests
          </p>
        </Card>
      )}

      {/* Add Test Dialog */}
      <Dialog open={showAddTest} onOpenChange={setShowAddTest}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Add Test to Tracker</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Test <span className="text-red-500">*</span></Label>
              <Select
                value={newTest.test_code}
                onValueChange={(value) => setNewTest({ ...newTest, test_code: value })}
              >
                <SelectTrigger className="rounded-lg" data-testid="select-test">
                  <SelectValue placeholder="Select test..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(availableTests).map(test => (
                    <SelectItem key={test} value={test}>
                      {availableTests[test]?.name || test}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Clinical Area</Label>
              <Select
                value={newTest.clinical_area}
                onValueChange={(value) => setNewTest({ ...newTest, clinical_area: value })}
              >
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder="Select area..." />
                </SelectTrigger>
                <SelectContent>
                  {CLINICAL_AREAS.map(area => (
                    <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Subtests to administer</Label>
              <Input
                value={newTest.subtests}
                onChange={(e) => setNewTest({ ...newTest, subtests: e.target.value })}
                placeholder="e.g., Vocabulary, Grammatical Morphemes (comma-separated)"
                className="rounded-lg"
              />
              {newTest.test_code && availableTests[newTest.test_code]?.subtests?.length > 0 && (
                <p className="text-xs text-stone-500">
                  Available: {availableTests[newTest.test_code].subtests.join(', ')}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTest(false)}>Cancel</Button>
            <Button onClick={addPlannedTest} className="rounded-full" data-testid="confirm-add-test">
              Add Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Session Dialog */}
      <Dialog open={showAddSession} onOpenChange={setShowAddSession}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Log Assessment Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Session Date</Label>
              <Input
                type="date"
                value={newSession.date}
                onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                className="rounded-lg"
                data-testid="add-session-date"
              />
              <p className="text-[11px] text-stone-500">
                You can backdate this if you're logging it after the session.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Session Notes</Label>
              <Textarea
                value={newSession.notes}
                onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                placeholder="What was accomplished in this session? How did the child engage?"
                className="min-h-24 rounded-lg"
              />
            </div>
            <div
              className="text-xs text-emerald-800 bg-emerald-50/70 border border-emerald-200/70 rounded-lg px-3 py-2"
              data-testid="add-session-soap-hint"
            >
              We'll also save this as a SOAP note in your records — no need to write a second note for this session.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSession(false)}>Cancel</Button>
            <Button onClick={addSession} className="rounded-full" data-testid="confirm-add-session">
              Log Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AssessmentModule({ onLogout }) {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [availableTests, setAvailableTests] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [viewingAssessment, setViewingAssessment] = useState(null);
  // Controls which top-level tab is active. Made stateful (was defaultValue
  // only) so post-create-from-tracker we can hop back to Assessments and
  // surface the new row for the user immediately.
  const [assessmentsActiveTab, setAssessmentsActiveTab] = useState('assessments');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportStyle, setReportStyle] = useState('parent_friendly');
  const [frameworkPresets, setFrameworkPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [letterhead, setLetterhead] = useState(null);  // Add letterhead state  
  // Available assessment frameworks
  const ASSESSMENT_FRAMEWORKS = [
    { value: 'standard', label: 'Standard Clinical Assessment', description: 'Traditional SLP assessment with standardized and non-standardized measures' },
    { value: 'dir_floortime', label: 'DIR Floortime / Developmental Model', description: 'Focuses on Functional Emotional Developmental Capacities (FEDCs)' },
    { value: 'dasel', label: 'DASEL', description: 'Dynamic Assessment of Social Emotional Learning' },
    { value: 'scerts', label: 'SCERTS Framework', description: 'Social Communication, Emotional Regulation, Transactional Support' },
    { value: 'nla', label: 'NLA Stages (Natural Language Acquisition)', description: 'For Gestalt Language Processors - tracks NLA stages 1-4' },
    { value: 'custom', label: 'Custom Framework', description: 'Describe your own assessment approach' }
  ];
  
  // Fetch saved framework presets
  const fetchFrameworkPresets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/framework-presets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrameworkPresets(response.data);
    } catch (error) {
      console.error('Failed to fetch framework presets:', error);
    }
  };
  
  // Save current custom framework as a preset
  const saveFrameworkPreset = async () => {
    if (!newPresetName.trim() || !formData.assessment_framework_custom.trim()) {
      toast.error('Please enter a name and description for the preset');
      return;
    }
    
    setSavingPreset(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/framework-presets`, {
        name: newPresetName,
        description: formData.assessment_framework_custom
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Framework preset saved!');
      setNewPresetName('');
      fetchFrameworkPresets();
    } catch (error) {
      toast.error('Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };
  
  // Delete a framework preset
  const deleteFrameworkPreset = async (presetId) => {
    if (!window.confirm('Delete this framework preset?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/framework-presets/${presetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preset deleted');
      fetchFrameworkPresets();
    } catch (error) {
      toast.error('Failed to delete preset');
    }
  };
  
  // Load a preset into the form
  const loadFrameworkPreset = (preset) => {
    setFormData({ 
      ...formData, 
      assessment_framework: 'custom',
      assessment_framework_custom: preset.description 
    });
    toast.success(`Loaded "${preset.name}" framework`);
  };
  
  // Form state
  const [formData, setFormData] = useState({
    assessment_date: new Date().toISOString().split('T')[0],
    assessment_type: 'initial',
    // Assessment Framework
    assessment_framework: 'standard',
    assessment_framework_custom: '', // Description if custom selected
    // Language Learner Profile (required for neurodiversity-affirming interpretation)
    language_learner_profile: '',
    language_learner_profile_other: '',
    reason_for_referral: '',
    background_info: '',
    medical_history: '',
    developmental_history: '',
    standardized_scores: [],
    non_standardized_measures: [],
    language_sample_analysis: '',
    include_chunk_bank_data: false,
    standardized_testing_attempted: true, // Whether standardized testing was attempted
    standardized_testing_notes: '', // Why standardized testing wasn't possible/tolerated
    clinical_impressions: '',
    diagnoses: [], // Array of selected diagnoses
    diagnosis_notes: '', // Additional notes about diagnoses
    recommendations: [''],
    goals: [''],
    status: 'draft',
    // Section observations
    attention_observations: '',
    behaviour_observations: '',
    prelinguistic_observations: '',
    receptive_observations: '',
    expressive_observations: '',
    social_emotional_observations: '',
    speech_observations: '',
    articulation_notes: '',
    // Auditory Processing
    auditory_discrimination: '',
    auditory_memory: '',
    auditory_sequencing: '',
    listening_comprehension: '',
    auditory_notes: '',
    // Literacy & Phonological Awareness
    literacy_observations: '',
    phonological_awareness_notes: '',
    reading_notes: '',
    spelling_notes: '',
    // PROMPT Hierarchy
    prompt_jaw: '',
    prompt_lip: '',
    prompt_facial: '',
    prompt_lingual: '',
    prompt_sequencing: '',
    prompt_level: '',
    prompt_cueing: '',
    prompt_observations: '',
    // AAC Assessment
    aac_system_type: '',
    aac_device_name: '',
    aac_access_method: '',
    aac_vocabulary_type: '',
    aac_grid_size: '',
    aac_current_level: '',
    aac_barriers: '',
    aac_supports_needed: '',
    aac_observations: ''
  });

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [childRes, assessmentsRes, testsRes, letterheadRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`, { headers }),
        axios.get(`${API}/assessments/${childId}`, { headers }),
        axios.get(`${API}/assessments/tests`, { headers }),
        axios.get(`${API}/therapist/letterhead`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setChild(childRes.data);
      setAssessments(assessmentsRes.data);
      setAvailableTests(testsRes.data.tests || {});
      setLetterhead(letterheadRes.data);
      
      // Also fetch framework presets
      fetchFrameworkPresets();
    } catch (error) {
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      assessment_date: new Date().toISOString().split('T')[0],
      assessment_type: 'initial',
      language_learner_profile: '',
      language_learner_profile_other: '',
      reason_for_referral: '',
      background_info: '',
      medical_history: '',
      developmental_history: '',
      standardized_scores: [],
      non_standardized_measures: [],
      language_sample_analysis: '',
      include_chunk_bank_data: false,
      clinical_impressions: '',
      diagnoses: [],
      diagnosis_notes: '',
      recommendations: [''],
      goals: [''],
      status: 'draft'
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        child_id: childId,
        recommendations: formData.recommendations.filter(r => r.trim()),
        goals: formData.goals.filter(g => g.trim())
      };
      
      await axios.post(`${API}/assessments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Assessment created successfully');
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assessment');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingAssessment) return;
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        recommendations: formData.recommendations.filter(r => r.trim()),
        goals: formData.goals.filter(g => g.trim())
      };
      
      await axios.put(`${API}/assessments/${editingAssessment.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Assessment updated successfully');
      setEditingAssessment(null);
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assessment');
    }
  };

  const handleDelete = async (assessmentId) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assessments/${assessmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Assessment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete assessment');
    }
  };

  const startEdit = (assessment) => {
    setFormData({
      assessment_date: assessment.assessment_date || '',
      assessment_type: assessment.assessment_type || 'initial',
      language_learner_profile: assessment.language_learner_profile || '',
      language_learner_profile_other: assessment.language_learner_profile_other || '',
      assessment_framework: assessment.assessment_framework || 'standard',
      assessment_framework_custom: assessment.assessment_framework_custom || '',
      standardized_testing_attempted: assessment.standardized_testing_attempted !== false,
      standardized_testing_notes: assessment.standardized_testing_notes || '',
      reason_for_referral: assessment.reason_for_referral || '',
      background_info: assessment.background_info || '',
      medical_history: assessment.medical_history || '',
      developmental_history: assessment.developmental_history || '',
      standardized_scores: assessment.standardized_scores || [],
      non_standardized_measures: assessment.non_standardized_measures || [],
      language_sample_analysis: assessment.language_sample_analysis || '',
      include_chunk_bank_data: assessment.include_chunk_bank_data || false,
      clinical_impressions: assessment.clinical_impressions || '',
      diagnoses: assessment.diagnoses || [],
      diagnosis_notes: assessment.diagnosis_notes || assessment.diagnosis || '', // Backwards compat with old diagnosis field
      recommendations: assessment.recommendations?.length ? assessment.recommendations : [''],
      goals: assessment.goals?.length ? assessment.goals : [''],
      status: assessment.status || 'draft',
      // Section observations
      attention_observations: assessment.attention_observations || '',
      behaviour_observations: assessment.behaviour_observations || '',
      prelinguistic_observations: assessment.prelinguistic_observations || '',
      receptive_observations: assessment.receptive_observations || '',
      expressive_observations: assessment.expressive_observations || '',
      social_emotional_observations: assessment.social_emotional_observations || '',
      speech_observations: assessment.speech_observations || '',
      articulation_notes: assessment.articulation_notes || '',
      // Auditory Processing
      auditory_discrimination: assessment.auditory_discrimination || '',
      auditory_memory: assessment.auditory_memory || '',
      auditory_sequencing: assessment.auditory_sequencing || '',
      listening_comprehension: assessment.listening_comprehension || '',
      auditory_notes: assessment.auditory_notes || '',
      // Literacy
      literacy_observations: assessment.literacy_observations || '',
      phonological_awareness_notes: assessment.phonological_awareness_notes || '',
      reading_notes: assessment.reading_notes || '',
      spelling_notes: assessment.spelling_notes || '',
      // PROMPT Hierarchy
      prompt_jaw: assessment.prompt_jaw || '',
      prompt_lip: assessment.prompt_lip || '',
      prompt_facial: assessment.prompt_facial || '',
      prompt_lingual: assessment.prompt_lingual || '',
      prompt_sequencing: assessment.prompt_sequencing || '',
      prompt_level: assessment.prompt_level || '',
      prompt_cueing: assessment.prompt_cueing || '',
      prompt_observations: assessment.prompt_observations || '',
      // AAC Assessment
      aac_system_type: assessment.aac_system_type || '',
      aac_device_name: assessment.aac_device_name || '',
      aac_access_method: assessment.aac_access_method || '',
      aac_vocabulary_type: assessment.aac_vocabulary_type || '',
      aac_grid_size: assessment.aac_grid_size || '',
      aac_current_level: assessment.aac_current_level || '',
      aac_barriers: assessment.aac_barriers || '',
      aac_supports_needed: assessment.aac_supports_needed || '',
      aac_observations: assessment.aac_observations || ''
    });
    setEditingAssessment(assessment);
    setShowCreate(true);
  };

  const generateReport = async (assessmentId) => {
    setGeneratingReport(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/assessments/generate-report`, {
        assessment_id: assessmentId,
        include_chunk_analysis: true,
        report_style: reportStyle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if generation started in background
      if (response.data.status === 'generating') {
        toast.success('Report generation started! This usually takes 30-60 seconds...');
        
        // Poll for completion
        let attempts = 0;
        const maxAttempts = 25; // 25 attempts * 3 seconds = 75 seconds max
        
        const checkStatus = async () => {
          attempts++;
          try {
            const checkResponse = await axios.get(`${API}/assessments/detail/${assessmentId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            const status = checkResponse.data.report_status;
            
            if ((status === 'complete' || status === 'complete_fallback') && checkResponse.data.generated_report) {
              if (status === 'complete_fallback') {
                toast.success('Report generated! (Summary version due to timeout)');
              } else {
                toast.success('Report generated successfully!');
              }
              setViewingAssessment(checkResponse.data);
              setGeneratingReport(false);
              fetchData();
              return;
            } else if (status === 'error') {
              setViewingAssessment(checkResponse.data);
              setGeneratingReport(false);
              toast.error('Report generation failed. You can try again.');
              return;
            } else if (status === 'generating_fallback') {
              // Still generating fallback, keep waiting
              if (attempts < maxAttempts) {
                setTimeout(checkStatus, 3000);
              }
            } else if (attempts < maxAttempts) {
              // Still generating, check again
              setTimeout(checkStatus, 3000);
            } else {
              // Max attempts reached, check one more time
              const finalCheck = await axios.get(`${API}/assessments/detail/${assessmentId}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (finalCheck.data.generated_report) {
                toast.success('Report generated!');
                setViewingAssessment(finalCheck.data);
              } else {
                toast.error('Report is taking longer than expected. Please refresh to check status.');
              }
              setGeneratingReport(false);
            }
          } catch (err) {
            console.error('Status check error:', err);
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 3000);
            } else {
              setGeneratingReport(false);
            }
          }
        };
        
        // Start polling after 3 seconds
        setTimeout(checkStatus, 3000);
        return;
      }
      
      // Immediate response (shouldn't happen with new flow but handle it)
      if (response.data.report) {
        toast.success('Report generated successfully!');
        setViewingAssessment({
          ...viewingAssessment,
          generated_report: response.data.report,
          report_generated_at: response.data.generated_at
        });
        fetchData();
        setGeneratingReport(false);
      }
    } catch (error) {
      console.error('Report generation error:', error);
      setGeneratingReport(false);
      
      if (error.response?.status === 429) {
        toast.error('AI usage limit reached. Please try again later or upgrade your plan.');
      } else {
        toast.error('Failed to start report generation. Please try again.');
      }
    }
  };

  const copyReport = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Report copied to clipboard');
  };

  const printReport = (report, assessment) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Assessment Report - ${child?.name || 'Child'}</title>
          <style>
            body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; }
            h1 { color: #4F46E5; border-bottom: 2px solid #4F46E5; padding-bottom: 10px; }
            h2 { color: #1F2937; margin-top: 24px; }
            p { margin: 8px 0; }
            .meta { color: #6B7280; font-size: 14px; margin-bottom: 20px; }
            pre { white-space: pre-wrap; font-family: inherit; }
          </style>
        </head>
        <body>
          <h1>Speech-Language Assessment Report</h1>
          <div class="meta">
            <p>Client: ${child?.name || 'N/A'} | Date: ${assessment?.assessment_date || 'N/A'}</p>
            <p>Assessment Type: ${ASSESSMENT_TYPES.find(t => t.value === assessment?.assessment_type)?.label || assessment?.assessment_type}</p>
          </div>
          <pre>${report}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const exportDocx = async (assessment) => {
    if (!assessment?.id) {
      toast.error('No assessment selected');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/assessments/${assessment.id}/export-docx`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      let filename = `Assessment_Report_${(child?.name || 'report').replace(/[^A-Za-z0-9_-]+/g, '_')}.docx`;
      const cd = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
      if (cd) {
        const m = /filename="?([^"]+)"?/i.exec(cd);
        if (m && m[1]) filename = m[1];
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Word document downloaded');
    } catch (error) {
      let msg = 'Failed to export Word document';
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json?.detail) msg = json.detail;
        } else if (error?.response?.data?.detail) {
          msg = error.response.data.detail;
        }
      } catch (_) {
        // ignore parse failure
      }
      toast.error(msg);
    }
  };

  // Add standardized score
  const addStandardizedScore = (clinicalArea = '') => {
    setFormData(prev => ({
      ...prev,
      standardized_scores: [...prev.standardized_scores, {
        clinical_area: clinicalArea,  // Which section this score belongs to
        test_code: '',
        subtest_name: '',
        raw_score: null,
        score_type: 'standard',  // 'standard' or 'scaled'
        converted_score: null,
        standard_score: null,
        percentile_rank: null,
        age_equivalent: '',
        scaled_score: null,
        errors_observed: '', // Specific errors the child made
        notes: ''
      }]
    }));
  };

  const updateStandardizedScore = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.standardized_scores];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, standardized_scores: updated };
    });
  };

  const removeStandardizedScore = (index) => {
    setFormData(prev => ({
      ...prev,
      standardized_scores: prev.standardized_scores.filter((_, i) => i !== index)
    }));
  };

  // Add non-standardized measure
  const addNonStandardizedMeasure = (clinicalArea = '') => {
    setFormData(prev => ({
      ...prev,
      non_standardized_measures: [...prev.non_standardized_measures, {
        clinical_area: clinicalArea,  // Which section this measure belongs to
        measure_type: 'observation',
        title: '',
        description: '',
        findings: '',
        strengths_noted: [],
        areas_for_growth: []
      }]
    }));
  };

  const updateNonStandardizedMeasure = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.non_standardized_measures];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, non_standardized_measures: updated };
    });
  };

  const removeNonStandardizedMeasure = (index) => {
    setFormData(prev => ({
      ...prev,
      non_standardized_measures: prev.non_standardized_measures.filter((_, i) => i !== index)
    }));
  };

  // Clinical areas for the assessment
  const CLINICAL_AREAS = [
    { id: 'prelinguistic', name: 'Prelinguistic Skills', color: 'indigo', description: 'Joint attention, gestures, turn-taking, imitation' },
    { id: 'receptive', name: 'Receptive Language', color: 'blue' },
    { id: 'expressive', name: 'Expressive Language', color: 'green', hasLanguageSample: true },
    { id: 'auditory', name: 'Auditory Processing', color: 'purple' },
    { id: 'literacy', name: 'Literacy', color: 'amber' },
    { id: 'social_emotional', name: 'Social-Emotional Skills', color: 'pink' },
    { id: 'speech_motor', name: 'Speech & Oral Motor Planning', color: 'orange', hasPrompt: true },
    { id: 'aac', name: 'AAC', color: 'cyan', isAac: true },
    { id: 'feeding', name: 'Feeding & Swallowing', color: 'teal' }
  ];

  // Get scores for a specific clinical area
  const getScoresForArea = (areaId) => {
    return formData.standardized_scores
      .map((score, index) => ({ ...score, originalIndex: index }))
      .filter(score => score.clinical_area === areaId);
  };

  // Get non-standardized measures for a specific clinical area
  const getMeasuresForArea = (areaId) => {
    return formData.non_standardized_measures
      .map((measure, index) => ({ ...measure, originalIndex: index }))
      .filter(measure => measure.clinical_area === areaId);
  };

  // Get scores without a clinical area assigned (legacy/unassigned)
  const getUnassignedScores = () => {
    return formData.standardized_scores
      .map((score, index) => ({ ...score, originalIndex: index }))
      .filter(score => !score.clinical_area || score.clinical_area === '');
  };

  // Get measures without a clinical area assigned (legacy/unassigned)
  const getUnassignedMeasures = () => {
    return formData.non_standardized_measures
      .map((measure, index) => ({ ...measure, originalIndex: index }))
      .filter(measure => !measure.clinical_area || measure.clinical_area === '');
  };

  // Render standardized tests for a clinical area
  const renderStandardizedTestsForArea = (areaId, areaName) => {
    const areaScores = getScoresForArea(areaId);
    return (
      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value={`${areaId}-tests`} className="border rounded-lg px-3 bg-white">
          <AccordionTrigger className="hover:no-underline py-3">
            <span className="text-sm font-medium">Standardized Tests ({areaScores.length})</span>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            {areaScores.map((score) => (
              <Card key={score.originalIndex} className="p-3 bg-stone-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-medium text-stone-500">Test #{areaScores.indexOf(score) + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStandardizedScore(score.originalIndex)}
                    className="text-red-500 h-6 w-6 p-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Test</Label>
                    <Select
                      value={score.test_code}
                      onValueChange={(value) => updateStandardizedScore(score.originalIndex, 'test_code', value)}
                    >
                      <SelectTrigger className="h-9 rounded-lg text-sm">
                        <SelectValue placeholder="Select test" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(availableTests).map(test => (
                          <SelectItem key={test} value={test}>{test}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Subtest</Label>
                    <Select
                      value={score.subtest_name}
                      onValueChange={(value) => updateStandardizedScore(score.originalIndex, 'subtest_name', value)}
                      disabled={!score.test_code}
                    >
                      <SelectTrigger className="h-9 rounded-lg text-sm">
                        <SelectValue placeholder="Select subtest" />
                      </SelectTrigger>
                      <SelectContent>
                        {score.test_code && availableTests[score.test_code]?.subtests?.map(subtest => (
                          <SelectItem key={subtest} value={subtest}>{subtest}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Score Type</Label>
                    <Select
                      value={score.score_type || 'standard'}
                      onValueChange={(value) => updateStandardizedScore(score.originalIndex, 'score_type', value)}
                    >
                      <SelectTrigger className="h-9 rounded-lg text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard Score</SelectItem>
                        <SelectItem value="scaled">Scaled Score</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{(score.score_type || 'standard') === 'scaled' ? 'Scaled Score' : 'Standard Score'}</Label>
                    <Input
                      type="number"
                      value={score.converted_score ?? ''}
                      onChange={(e) => updateStandardizedScore(score.originalIndex, 'converted_score', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder={(score.score_type || 'standard') === 'scaled' ? '1-19' : '40-160'}
                      className="h-9 rounded-lg text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Percentile</Label>
                    <Input
                      type="number"
                      value={score.percentile_rank || ''}
                      onChange={(e) => updateStandardizedScore(score.originalIndex, 'percentile_rank', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="1-99"
                      className="h-9 rounded-lg text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Age Equivalent</Label>
                    <Input
                      value={score.age_equivalent || ''}
                      onChange={(e) => updateStandardizedScore(score.originalIndex, 'age_equivalent', e.target.value)}
                      placeholder="e.g., 3;6"
                      className="h-9 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  <Label className="text-xs">Notes/Observations</Label>
                  <Textarea
                    value={score.notes || ''}
                    onChange={(e) => updateStandardizedScore(score.originalIndex, 'notes', e.target.value)}
                    placeholder="Error patterns, observations..."
                    className="min-h-16 rounded-lg text-sm"
                  />
                </div>
              </Card>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addStandardizedScore(areaId)}
              className="w-full rounded-lg"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Test
            </Button>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  };

  // Render non-standardized measures for a clinical area
  const renderNonStandardizedMeasuresForArea = (areaId, areaName) => {
    const areaMeasures = getMeasuresForArea(areaId);
    const guidedPrompts = INFORMAL_ASSESSMENT_PROMPTS[areaId];
    
    return (
      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value={`${areaId}-measures`} className="border rounded-lg px-3 bg-white">
          <AccordionTrigger className="hover:no-underline py-3">
            <span className="text-sm font-medium">Non-Standardized Measures ({areaMeasures.length})</span>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            {/* Guided Prompts Section */}
            {guidedPrompts && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 space-y-2">
                <div className="flex items-start gap-2">
                  <BookOpen className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-blue-800 mb-1">
                      What to observe for {guidedPrompts.title}:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {guidedPrompts.prompts.map((prompt, idx) => (
                        <Badge 
                          key={idx} 
                          variant="secondary" 
                          className="text-xs bg-white text-blue-700 border border-blue-200 font-normal"
                        >
                          {prompt}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Example Data Entry */}
                {guidedPrompts.example && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <p className="text-xs text-blue-700 italic">
                      {guidedPrompts.example}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {areaMeasures.map((measure) => (
              <Card key={measure.originalIndex} className="p-3 bg-stone-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-medium text-stone-500">Measure #{areaMeasures.indexOf(measure) + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeNonStandardizedMeasure(measure.originalIndex)}
                    className="text-red-500 h-6 w-6 p-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={measure.measure_type}
                        onValueChange={(value) => updateNonStandardizedMeasure(measure.originalIndex, 'measure_type', value)}
                      >
                        <SelectTrigger className="h-9 rounded-lg text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MEASURE_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Title</Label>
                      <Input
                        value={measure.title}
                        onChange={(e) => updateNonStandardizedMeasure(measure.originalIndex, 'title', e.target.value)}
                        placeholder="Name of measure"
                        className="h-9 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Findings</Label>
                    <Textarea
                      value={measure.findings}
                      onChange={(e) => updateNonStandardizedMeasure(measure.originalIndex, 'findings', e.target.value)}
                      placeholder={guidedPrompts 
                        ? `Document observations about: ${guidedPrompts.prompts.slice(0, 3).join(', ')}...`
                        : "Observations and findings..."}
                      className="min-h-20 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </Card>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addNonStandardizedMeasure(areaId)}
              className="w-full rounded-lg"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Measure
            </Button>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  };


  // Render form
  const renderForm = () => (
    <form onSubmit={editingAssessment ? handleUpdate : handleCreate} className="space-y-6">
      {/* Data Summary Banner (shows when editing and has data) */}
      {editingAssessment && (formData.standardized_scores.length > 0 || formData.non_standardized_measures.length > 0) && (
        <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-green-900">Assessment Data Summary</h3>
              <p className="text-sm text-green-700">
                {formData.standardized_scores.length > 0 && (
                  <span className="font-medium">{formData.standardized_scores.length} standardized test{formData.standardized_scores.length !== 1 ? 's' : ''}</span>
                )}
                {formData.standardized_scores.length > 0 && formData.non_standardized_measures.length > 0 && ' • '}
                {formData.non_standardized_measures.length > 0 && (
                  <span className="font-medium">{formData.non_standardized_measures.length} informal measure{formData.non_standardized_measures.length !== 1 ? 's' : ''}</span>
                )}
                {' — expand clinical areas below to view/edit'}
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Assessment Date</Label>
          <Input
            type="date"
            value={formData.assessment_date}
            onChange={(e) => setFormData({ ...formData, assessment_date: e.target.value })}
            className="h-12 rounded-xl"
            required
            data-testid="assessment-date"
          />
        </div>
        <div className="space-y-2">
          <Label>Assessment Type</Label>
          <Select
            value={formData.assessment_type}
            onValueChange={(value) => setFormData({ ...formData, assessment_type: value })}
          >
            <SelectTrigger className="h-12 rounded-xl" data-testid="assessment-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSESSMENT_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Assessment Framework Selector */}
      <Card className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200 rounded-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h3 className="font-medium text-teal-900">Assessment Framework</h3>
            <p className="text-sm text-teal-700 mt-1">
              Select the clinical approach for this assessment. This guides how the AI structures and writes the report.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-teal-900">Framework</Label>
            <Select
              value={formData.assessment_framework}
              onValueChange={(value) => setFormData({ ...formData, assessment_framework: value })}
            >
              <SelectTrigger className="h-12 rounded-xl bg-white border-teal-200" data-testid="assessment-framework">
                <SelectValue placeholder="Select assessment framework..." />
              </SelectTrigger>
              <SelectContent>
                {ASSESSMENT_FRAMEWORKS.map(framework => (
                  <SelectItem key={framework.value} value={framework.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{framework.label}</span>
                      <span className="text-xs text-stone-500">{framework.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {formData.assessment_framework === 'custom' && (
            <div className="space-y-3">
              {/* Saved Presets */}
              {frameworkPresets.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-teal-900 text-sm">Saved Presets</Label>
                  <div className="flex flex-wrap gap-2">
                    {frameworkPresets.map(preset => (
                      <div key={preset.id} className="flex items-center gap-1 bg-white rounded-lg border border-teal-200 pl-3 pr-1 py-1">
                        <button
                          type="button"
                          onClick={() => loadFrameworkPreset(preset)}
                          className="text-sm text-teal-700 hover:text-teal-900"
                        >
                          {preset.name}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteFrameworkPreset(preset.id)}
                          className="text-stone-400 hover:text-red-500 p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Framework Description */}
              <div className="space-y-2">
                <Label className="text-teal-900">Describe Your Framework</Label>
                <Textarea
                  value={formData.assessment_framework_custom}
                  onChange={(e) => setFormData({ ...formData, assessment_framework_custom: e.target.value })}
                  placeholder="Describe the assessment framework you're using. Include:&#10;• The name of the approach/model&#10;• Key sections or areas to assess&#10;• Terminology to use (e.g., 'capacities' vs 'skills')&#10;• Philosophy or principles to guide interpretation&#10;&#10;Example: 'Using the Hanen approach, focusing on responsive interaction strategies. Report should discuss the child's communication stage, caregiver-child interaction patterns, and environmental supports...'"
                  className="min-h-32 rounded-xl bg-white border-teal-200"
                  data-testid="assessment-framework-custom"
                />
              </div>
              
              {/* Save as Preset */}
              {formData.assessment_framework_custom.trim() && (
                <div className="flex items-center gap-2 p-3 bg-white/60 rounded-xl">
                  <Input
                    value={newPresetName}
                    onChange={(e) => setNewPresetName(e.target.value)}
                    placeholder="Preset name (e.g., 'Hanen Approach')"
                    className="flex-1 h-9 rounded-lg text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={saveFrameworkPreset}
                    disabled={savingPreset || !newPresetName.trim()}
                    className="rounded-lg border-teal-300 text-teal-700"
                  >
                    {savingPreset ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save Preset
                  </Button>
                </div>
              )}
            </div>
          )}
          {formData.assessment_framework && formData.assessment_framework !== 'standard' && formData.assessment_framework !== 'custom' && (
            <div className="p-3 bg-white/60 rounded-xl text-sm text-teal-800">
              <strong>Framework guidance:</strong>
              {formData.assessment_framework === 'dir_floortime' && (
                <span> The AI will structure findings around Functional Emotional Developmental Capacities (FEDCs), emphasizing circles of communication, affect signaling, and developmental progression.</span>
              )}
              {formData.assessment_framework === 'dasel' && (
                <span> The AI will focus on dynamic assessment principles, social-emotional learning domains, and the child's response to scaffolding and support.</span>
              )}
              {formData.assessment_framework === 'scerts' && (
                <span> The AI will organize findings around Social Communication, Emotional Regulation, and Transactional Support domains with emphasis on joint attention and symbol use stages.</span>
              )}
              {formData.assessment_framework === 'nla' && (
                <span> The AI will analyze language through NLA stages 1-4, documenting gestalt forms, mitigations, and progression toward self-generated language.</span>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Language Learner Profile - Required for Neurodiversity-Affirming Interpretation */}
      <Card className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 rounded-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <Heart className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-medium text-purple-900">Language Learner Profile</h3>
            <p className="text-sm text-purple-700 mt-1">
              Select the child's language learning style. This guides AI interpretation to be strengths-based and neurodiversity-affirming.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-purple-900">Profile Type <span className="text-red-500">*</span></Label>
            <Select
              value={formData.language_learner_profile}
              onValueChange={(value) => setFormData({ ...formData, language_learner_profile: value, language_learner_profile_other: value === 'Other' ? formData.language_learner_profile_other : '' })}
            >
              <SelectTrigger className="h-12 rounded-xl bg-white border-purple-200" data-testid="language-learner-profile">
                <SelectValue placeholder="Select language learner profile..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GLP">
                  <div className="flex flex-col">
                    <span className="font-medium">Gestalt Language Processor (GLP)</span>
                    <span className="text-xs text-stone-500">Learns language in chunks, scripts, or gestalts</span>
                  </div>
                </SelectItem>
                <SelectItem value="Analytical">
                  <div className="flex flex-col">
                    <span className="font-medium">Analytical Language Learner</span>
                    <span className="text-xs text-stone-500">Builds language word-by-word through rules</span>
                  </div>
                </SelectItem>
                <SelectItem value="Mixed">
                  <div className="flex flex-col">
                    <span className="font-medium">Mixed Profile</span>
                    <span className="text-xs text-stone-500">Uses both gestalt and analytical processing</span>
                  </div>
                </SelectItem>
                <SelectItem value="AAC">
                  <div className="flex flex-col">
                    <span className="font-medium">AAC User</span>
                    <span className="text-xs text-stone-500">Communicates across multiple modalities</span>
                  </div>
                </SelectItem>
                <SelectItem value="Other">
                  <div className="flex flex-col">
                    <span className="font-medium">Other</span>
                    <span className="text-xs text-stone-500">Describe the child's unique profile</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.language_learner_profile === 'Other' && (
            <div className="space-y-2">
              <Label className="text-purple-900">Describe Profile</Label>
              <Textarea
                value={formData.language_learner_profile_other}
                onChange={(e) => setFormData({ ...formData, language_learner_profile_other: e.target.value })}
                placeholder="Describe the child's unique language learning profile and communication style..."
                className="min-h-20 rounded-xl bg-white border-purple-200"
                data-testid="language-learner-profile-other"
              />
            </div>
          )}
          {formData.language_learner_profile && (
            <div className="p-3 bg-white/60 rounded-xl text-sm text-purple-800">
              <strong>How this affects interpretation:</strong>
              {formData.language_learner_profile === 'GLP' && (
                <span> AI will recognize gestalts as meaningful communication, estimate GLP stage, and interpret echolalia as a language learning strategy.</span>
              )}
              {formData.language_learner_profile === 'Analytical' && (
                <span> AI will focus on vocabulary, syntax, and rule-based language development patterns.</span>
              )}
              {formData.language_learner_profile === 'Mixed' && (
                <span> AI will acknowledge both gestalt and analytical features and interpret strengths across both systems.</span>
              )}
              {formData.language_learner_profile === 'AAC' && (
                <span> AI will interpret communication across all modalities and emphasize communicative competence over speech-only measures.</span>
              )}
              {formData.language_learner_profile === 'Other' && (
                <span> AI will use your description to guide interpretation using strengths-based, affirming principles.</span>
              )}
            </div>
          )}
        </div>
      </Card>

      <Accordion type="multiple" defaultValue={['background', 'tests']} className="space-y-2">
        {/* Background Information */}
        <AccordionItem value="background" className="border rounded-xl px-4">
          <AccordionTrigger className="hover:no-underline">
            <span className="font-medium">Background Information</span>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <div className="space-y-2">
              <Label>Reason for Referral</Label>
              <Textarea
                value={formData.reason_for_referral}
                onChange={(e) => setFormData({ ...formData, reason_for_referral: e.target.value })}
                placeholder="Why was the child referred for evaluation?"
                className="min-h-20 rounded-xl"
                data-testid="reason-referral"
              />
            </div>
            <div className="space-y-2">
              <Label>Background Information</Label>
              <Textarea
                value={formData.background_info}
                onChange={(e) => setFormData({ ...formData, background_info: e.target.value })}
                placeholder="Family history, educational setting, therapy history..."
                className="min-h-24 rounded-xl"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Medical History</Label>
                <Textarea
                  value={formData.medical_history}
                  onChange={(e) => setFormData({ ...formData, medical_history: e.target.value })}
                  placeholder="Relevant medical information..."
                  className="min-h-20 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Developmental History</Label>
                <Textarea
                  value={formData.developmental_history}
                  onChange={(e) => setFormData({ ...formData, developmental_history: e.target.value })}
                  placeholder="Motor milestones, language development..."
                  className="min-h-20 rounded-xl"
                />
              </div>
            </div>
            
            {/* Standardized Testing Tolerance */}
            <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="standardized_not_possible"
                  checked={!formData.standardized_testing_attempted}
                  onChange={(e) => setFormData({ ...formData, standardized_testing_attempted: !e.target.checked })}
                  className="mt-1 rounded"
                />
                <div className="flex-1">
                  <Label htmlFor="standardized_not_possible" className="text-amber-900 font-medium cursor-pointer">
                    Standardized testing was not possible/tolerated
                  </Label>
                  <p className="text-xs text-amber-700 mt-1">
                    Check this if the child could not complete standardized assessments. The report will focus on observational and non-standardized data.
                  </p>
                  {!formData.standardized_testing_attempted && (
                    <Textarea
                      value={formData.standardized_testing_notes}
                      onChange={(e) => setFormData({ ...formData, standardized_testing_notes: e.target.value })}
                      placeholder="Describe why standardized testing wasn't possible (e.g., child's regulation, attention, sensory needs, age-appropriateness...)"
                      className="mt-2 min-h-16 rounded-lg text-sm bg-white"
                    />
                  )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Clinical Assessment Areas */}
        {CLINICAL_AREAS.map(area => {
          const areaScoreCount = getScoresForArea(area.id).length;
          const areaMeasureCount = getMeasuresForArea(area.id).length;
          const hasData = areaScoreCount > 0 || areaMeasureCount > 0 || 
            (area.hasLanguageSample && formData.language_sample_analysis?.trim()) ||
            (area.hasPrompt && (formData.prompt_jaw || formData.prompt_lip || formData.prompt_lingual || formData.prompt_observations));
          
          return (
          <AccordionItem key={area.id} value={area.id} className={`border rounded-xl px-4 ${
            area.color === 'indigo' ? 'border-indigo-200 bg-indigo-50/30' :
            area.color === 'blue' ? 'border-blue-200 bg-blue-50/30' :
            area.color === 'green' ? 'border-green-200 bg-green-50/30' :
            area.color === 'purple' ? 'border-purple-200 bg-purple-50/30' :
            area.color === 'amber' ? 'border-amber-200 bg-amber-50/30' :
            area.color === 'pink' ? 'border-pink-200 bg-pink-50/30' :
            area.color === 'orange' ? 'border-orange-200 bg-orange-50/30' :
            area.color === 'cyan' ? 'border-cyan-200 bg-cyan-50/30' :
            area.color === 'teal' ? 'border-teal-200 bg-teal-50/30' :
            ''
          }`}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <span className={`font-medium ${
                  area.color === 'indigo' ? 'text-indigo-900' :
                  area.color === 'blue' ? 'text-blue-900' :
                  area.color === 'green' ? 'text-green-900' :
                  area.color === 'purple' ? 'text-purple-900' :
                  area.color === 'amber' ? 'text-amber-900' :
                  area.color === 'pink' ? 'text-pink-900' :
                  area.color === 'orange' ? 'text-orange-900' :
                  area.color === 'cyan' ? 'text-cyan-900' :
                  area.color === 'teal' ? 'text-teal-900' :
                  ''
                }`}>{area.name}</span>
                {hasData && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    area.color === 'indigo' ? 'bg-indigo-200 text-indigo-800' :
                    area.color === 'blue' ? 'bg-blue-200 text-blue-800' :
                    area.color === 'green' ? 'bg-green-200 text-green-800' :
                    area.color === 'purple' ? 'bg-purple-200 text-purple-800' :
                    area.color === 'amber' ? 'bg-amber-200 text-amber-800' :
                    area.color === 'pink' ? 'bg-pink-200 text-pink-800' :
                    area.color === 'orange' ? 'bg-orange-200 text-orange-800' :
                    area.color === 'cyan' ? 'bg-cyan-200 text-cyan-800' :
                    area.color === 'teal' ? 'bg-teal-200 text-teal-800' :
                    'bg-stone-200 text-stone-800'
                  }`}>
                    {areaScoreCount > 0 && `${areaScoreCount} test${areaScoreCount !== 1 ? 's' : ''}`}
                    {areaScoreCount > 0 && areaMeasureCount > 0 && ', '}
                    {areaMeasureCount > 0 && `${areaMeasureCount} measure${areaMeasureCount !== 1 ? 's' : ''}`}
                    {(areaScoreCount === 0 && areaMeasureCount === 0) && 'has data'}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-4">
              {/* Standardized Tests for this area */}
              {renderStandardizedTestsForArea(area.id, area.name)}
              
              {/* Non-Standardized Measures for this area */}
              {renderNonStandardizedMeasuresForArea(area.id, area.name)}
              
              {/* Language Sample Analysis (only for Expressive Language) */}
              {area.hasLanguageSample && (
                <Accordion type="multiple" className="space-y-2">
                  <AccordionItem value={`${area.id}-language-sample`} className="border rounded-lg px-3 bg-white">
                    <AccordionTrigger className="hover:no-underline py-3">
                      <span className="text-sm font-medium">Language Sample Analysis</span>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-3">
                      <Textarea
                        value={formData.language_sample_analysis}
                        onChange={(e) => setFormData({ ...formData, language_sample_analysis: e.target.value })}
                        placeholder="MLU, TTR, utterance types, GLP stage evidence..."
                        className="min-h-32 rounded-lg text-sm"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="include_chunks"
                          checked={formData.include_chunk_bank_data}
                          onChange={(e) => setFormData({ ...formData, include_chunk_bank_data: e.target.checked })}
                          className="rounded"
                        />
                        <Label htmlFor="include_chunks" className="text-sm">Include Chunk Bank data in report</Label>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
              
              {/* PROMPT Section (only for Speech & Motor) */}
              {area.hasPrompt && (
                <Accordion type="multiple" className="space-y-2">
                  <AccordionItem value={`${area.id}-prompt`} className="border rounded-lg px-3 bg-white border-purple-200">
                    <AccordionTrigger className="hover:no-underline py-3">
                      <span className="text-sm font-medium text-purple-900">PROMPT Hierarchy</span>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-3">
                      <p className="text-xs text-purple-700">
                        Document motor speech observations using the PROMPT hierarchy.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Jaw Control</Label>
                          <Select value={formData.prompt_jaw} onValueChange={(v) => setFormData({...formData, prompt_jaw: v})}>
                            <SelectTrigger className="h-9 rounded-lg text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="adequate">Adequate</SelectItem>
                              <SelectItem value="emerging">Emerging</SelectItem>
                              <SelectItem value="requires_support">Requires Support</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Lip Control</Label>
                          <Select value={formData.prompt_lip} onValueChange={(v) => setFormData({...formData, prompt_lip: v})}>
                            <SelectTrigger className="h-9 rounded-lg text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="adequate">Adequate</SelectItem>
                              <SelectItem value="emerging">Emerging</SelectItem>
                              <SelectItem value="requires_support">Requires Support</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Lingual Control</Label>
                          <Select value={formData.prompt_lingual} onValueChange={(v) => setFormData({...formData, prompt_lingual: v})}>
                            <SelectTrigger className="h-9 rounded-lg text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="adequate">Adequate</SelectItem>
                              <SelectItem value="emerging">Emerging</SelectItem>
                              <SelectItem value="requires_support">Requires Support</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Sequencing</Label>
                          <Select value={formData.prompt_sequencing} onValueChange={(v) => setFormData({...formData, prompt_sequencing: v})}>
                            <SelectTrigger className="h-9 rounded-lg text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="adequate">Adequate</SelectItem>
                              <SelectItem value="emerging">Emerging</SelectItem>
                              <SelectItem value="requires_support">Requires Support</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">PROMPT Observations</Label>
                        <Textarea
                          value={formData.prompt_observations}
                          onChange={(e) => setFormData({...formData, prompt_observations: e.target.value})}
                          placeholder="Motor planning observations, tactile cueing response..."
                          className="min-h-20 rounded-lg text-sm"
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
              
              {/* AAC Section (only for AAC area) */}
              {area.isAac && (
                <div className="space-y-3 p-3 bg-white rounded-lg border">
                  <p className="text-xs text-cyan-700">
                    Document AAC system details and communication effectiveness.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">System Type</Label>
                      <Select value={formData.aac_system_type} onValueChange={(v) => setFormData({...formData, aac_system_type: v})}>
                        <SelectTrigger className="h-9 rounded-lg text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high_tech">High-Tech (iPad/Device)</SelectItem>
                          <SelectItem value="low_tech">Low-Tech (PECS, boards)</SelectItem>
                          <SelectItem value="mid_tech">Mid-Tech (GoTalk, etc.)</SelectItem>
                          <SelectItem value="none">No current system</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Device/App</Label>
                      <Input
                        value={formData.aac_device_name}
                        onChange={(e) => setFormData({...formData, aac_device_name: e.target.value})}
                        placeholder="e.g., TouchChat, LAMP"
                        className="h-9 rounded-lg text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Access Method</Label>
                      <Select value={formData.aac_access_method} onValueChange={(v) => setFormData({...formData, aac_access_method: v})}>
                        <SelectTrigger className="h-9 rounded-lg text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="direct_selection">Direct Selection</SelectItem>
                          <SelectItem value="switch">Switch Access</SelectItem>
                          <SelectItem value="eye_gaze">Eye Gaze</SelectItem>
                          <SelectItem value="partner_assisted">Partner Assisted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Grid Size</Label>
                      <Input
                        value={formData.aac_grid_size}
                        onChange={(e) => setFormData({...formData, aac_grid_size: e.target.value})}
                        placeholder="e.g., 4x4, 8x10"
                        className="h-9 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">AAC Observations</Label>
                    <Textarea
                      value={formData.aac_observations}
                      onChange={(e) => setFormData({...formData, aac_observations: e.target.value})}
                      placeholder="Communication effectiveness, vocabulary use, barriers..."
                      className="min-h-20 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
          );
        })}

        {/* Unassigned Tests/Measures (for backward compatibility) */}
        {(getUnassignedScores().length > 0 || getUnassignedMeasures().length > 0) && (
          <AccordionItem value="unassigned" className="border rounded-xl px-4 border-amber-300 bg-amber-50/50">
            <AccordionTrigger className="hover:no-underline">
              <span className="font-medium text-amber-900">
                Unassigned Tests/Measures ({getUnassignedScores().length + getUnassignedMeasures().length})
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-4">
              <p className="text-sm text-amber-700 bg-amber-100 p-3 rounded-lg">
                These tests/measures were created before clinical areas were added. Please assign them to a clinical area for better report organization.
              </p>
              
              {getUnassignedScores().length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-amber-900">Standardized Tests</h4>
                  {getUnassignedScores().map((score) => (
                    <Card key={score.originalIndex} className="p-3 bg-white rounded-lg">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{score.test_code} - {score.subtest_name || 'Total'}</p>
                          <p className="text-xs text-stone-500">
                            {(score.score_type || 'standard') === 'scaled' ? 'Scaled' : 'Standard'} Score: {score.converted_score ?? score.standard_score ?? '-'}
                          </p>
                        </div>
                        <Select
                          value={score.clinical_area || ''}
                          onValueChange={(value) => updateStandardizedScore(score.originalIndex, 'clinical_area', value)}
                        >
                          <SelectTrigger className="w-40 h-8 text-xs">
                            <SelectValue placeholder="Assign to area..." />
                          </SelectTrigger>
                          <SelectContent>
                            {CLINICAL_AREAS.map(area => (
                              <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
              
              {getUnassignedMeasures().length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-amber-900">Non-Standardized Measures</h4>
                  {getUnassignedMeasures().map((measure) => (
                    <Card key={measure.originalIndex} className="p-3 bg-white rounded-lg">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{measure.title || measure.measure_type}</p>
                          <p className="text-xs text-stone-500 line-clamp-1">{measure.findings}</p>
                        </div>
                        <Select
                          value={measure.clinical_area || ''}
                          onValueChange={(value) => updateNonStandardizedMeasure(measure.originalIndex, 'clinical_area', value)}
                        >
                          <SelectTrigger className="w-40 h-8 text-xs">
                            <SelectValue placeholder="Assign to area..." />
                          </SelectTrigger>
                          <SelectContent>
                            {CLINICAL_AREAS.map(area => (
                              <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Clinical Impressions */}
        <AccordionItem value="clinical" className="border rounded-xl px-4">
          <AccordionTrigger className="hover:no-underline">
            <span className="font-medium">Clinical Impressions & Diagnosis</span>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <div className="space-y-2">
              <Label>Clinical Impressions</Label>
              <Textarea
                value={formData.clinical_impressions}
                onChange={(e) => setFormData({ ...formData, clinical_impressions: e.target.value })}
                placeholder="Summarize clinical impressions, strengths, and areas of need..."
                className="min-h-28 rounded-xl"
                data-testid="clinical-impressions"
              />
            </div>
            <div className="space-y-3">
              <Label>Diagnosis / Suspected Diagnosis</Label>
              <p className="text-xs text-stone-500 mb-2">
                Select all that apply. This helps the AI contextualize interpretations appropriately.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'asd_diagnosed', label: 'ASD (Diagnosed)' },
                  { id: 'asd_suspected', label: 'Suspected ASD' },
                  { id: 'adhd', label: 'ADHD/ADD' },
                  { id: 'dld', label: 'Developmental Language Disorder (DLD)' },
                  { id: 'cas', label: 'Childhood Apraxia of Speech (CAS)' },
                  { id: 'ssd', label: 'Speech Sound Disorder' },
                  { id: 'intellectual_disability', label: 'Intellectual Disability' },
                  { id: 'hearing_impairment', label: 'Hearing Impairment' },
                  { id: 'genetic_syndrome', label: 'Genetic Syndrome' },
                  { id: 'global_developmental_delay', label: 'Global Developmental Delay' },
                  { id: 'neurotypical', label: 'Neurotypical / None' },
                ].map((dx) => (
                  <label key={dx.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-stone-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.diagnoses?.includes(dx.id)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...(formData.diagnoses || []), dx.id]
                          : (formData.diagnoses || []).filter(d => d !== dx.id);
                        setFormData({ ...formData, diagnoses: updated });
                      }}
                      className="rounded border-stone-300"
                    />
                    <span className="text-sm">{dx.label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-3">
                <Label className="text-sm">Other / Additional Notes</Label>
                <Textarea
                  value={formData.diagnosis_notes}
                  onChange={(e) => setFormData({ ...formData, diagnosis_notes: e.target.value })}
                  placeholder="Enter any other diagnoses or relevant information (e.g., specific syndrome name, suspected conditions under investigation)..."
                  className="min-h-16 rounded-xl mt-1"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Recommendations & Goals */}
        <AccordionItem value="recommendations" className="border rounded-xl px-4">
          <AccordionTrigger className="hover:no-underline">
            <span className="font-medium">Recommendations & Goals</span>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <div className="space-y-3">
              <Label>Recommendations</Label>
              {formData.recommendations.map((rec, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={rec}
                    onChange={(e) => {
                      const updated = [...formData.recommendations];
                      updated[index] = e.target.value;
                      setFormData({ ...formData, recommendations: updated });
                    }}
                    placeholder="Enter recommendation..."
                    className="flex-1 h-10 rounded-lg"
                  />
                  {formData.recommendations.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({
                        ...formData,
                        recommendations: formData.recommendations.filter((_, i) => i !== index)
                      })}
                      className="text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setFormData({ ...formData, recommendations: [...formData.recommendations, ''] })}
                className="text-primary"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Recommendation
              </Button>
            </div>
            <div className="space-y-3">
              <Label>Goals</Label>
              {formData.goals.map((goal, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={goal}
                    onChange={(e) => {
                      const updated = [...formData.goals];
                      updated[index] = e.target.value;
                      setFormData({ ...formData, goals: updated });
                    }}
                    placeholder="Enter goal..."
                    className="flex-1 h-10 rounded-lg"
                  />
                  {formData.goals.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({
                        ...formData,
                        goals: formData.goals.filter((_, i) => i !== index)
                      })}
                      className="text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setFormData({ ...formData, goals: [...formData.goals, ''] })}
                className="text-primary"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Goal
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setShowCreate(false);
            setEditingAssessment(null);
            resetForm();
          }}
          className="flex-1 rounded-full h-12"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 rounded-full h-12"
          data-testid="save-assessment"
        >
          <Save className="w-4 h-4 mr-2" />
          {editingAssessment ? 'Update Assessment' : 'Save Assessment'}
        </Button>
      </div>
    </form>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`/children/${childId}`)} className="rounded-full">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-serif text-primary-900">Assessments</h1>
                <p className="text-sm text-stone-500">{child?.name}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <IntroTutorial
              storageKey="tutorial-assessments-v1"
              title="Assessments & Reports"
              testid="tutorial-assessments"
              steps={[
                {
                  icon: <ClipboardCheck className="w-5 h-5" />,
                  title: 'Welcome to Assessments 💛',
                  body:
                    'This is your home for everything assessment-related — from setting up a tracker to writing the report. Each step is gentle and saves as you go.',
                },
                {
                  icon: <ListChecks className="w-5 h-5" />,
                  title: 'Assessment Tracker',
                  body:
                    'Use the Tracker tab to set the assessment plan: which tests you\u2019ll run and what you\u2019ll observe. Tap a row once to move it from \u201cTo do\u201d to \u201cIn progress\u201d, and tap it again to mark it Complete. The next tap archives it.',
                },
                {
                  icon: <PlayCircle className="w-5 h-5" />,
                  title: 'Capture observations',
                  body:
                    'Open an assessment to add scores, language samples, parent-report notes, or anything else you observed. Save as you go — PlayFirst doesn\u2019t mind half-finished drafts.',
                },
                {
                  icon: <Sparkles className="w-5 h-5" />,
                  title: 'AI-assisted report',
                  body:
                    'When you\u2019re ready, tap \u201cGenerate report\u201d. PlayFirst pulls everything together into a structured draft you can edit, copy, print or download as PDF.',
                },
                {
                  icon: <TrendingUp className="w-5 h-5" />,
                  title: 'You\u2019re ready',
                  body:
                    'No pressure to use every feature. Start with one tracker and build from there. Tap \u201cReplay tour\u201d anytime to see this again.',
                },
              ]}
            />
            {!showCreate && (
              <Button onClick={() => setShowCreate(true)} className="rounded-full" data-testid="create-assessment-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Assessment
              </Button>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {showCreate ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 bg-white rounded-3xl border border-stone-100">
                <h2 className="text-2xl font-serif text-primary-900 mb-6">
                  {editingAssessment ? 'Edit Assessment' : 'New Assessment'}
                </h2>
                {renderForm()}
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Tabs for Assessments vs Tracker vs Progress Reports */}
              <Tabs value={assessmentsActiveTab} onValueChange={setAssessmentsActiveTab} className="w-full">
                <TabsList className="bg-stone-100 p-1 rounded-xl mb-6">
                  <TabsTrigger 
                    value="assessments" 
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                    data-testid="assessments-tab"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                    Assessments
                  </TabsTrigger>
                  <TabsTrigger 
                    value="progress-reports" 
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                    data-testid="progress-reports-tab"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Progress Reports
                  </TabsTrigger>
                  <TabsTrigger 
                    value="tracker" 
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                    data-testid="tracker-tab"
                  >
                    <ListChecks className="w-4 h-4 mr-2" />
                    Tracker
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="assessments" className="space-y-6 mt-0">
                  {/* Info Banner */}
                  <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                    <div className="flex items-start gap-3">
                      <ClipboardCheck className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-900 font-medium">Assessment Module</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Track standardized and non-standardized assessments, import language samples from the Chunk Bank, 
                          and generate AI-powered comprehensive reports.
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Report Writing Style Section */}
                  <WritingStyleSection />

                  {/* Letterhead Section */}
                  <LetterheadSection />

                  {/* Assessment List */}
                  {assessments.length === 0 ? (
                    <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100">
                      <ClipboardCheck className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-stone-700 mb-2">No assessments yet</h3>
                      <p className="text-stone-500 mb-6">Create your first assessment to track evaluation data</p>
                      <Button onClick={() => setShowCreate(true)} className="rounded-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Assessment
                      </Button>
                    </Card>
              ) : (
                <div className="space-y-4">
                  {assessments.map((assessment) => (
                    <Card 
                      key={assessment.id} 
                      className="p-5 bg-white rounded-2xl border border-stone-100 hover:shadow-md transition-shadow"
                      data-testid={`assessment-card-${assessment.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge variant={assessment.status === 'complete' ? 'default' : 'secondary'}>
                              {assessment.status === 'complete' ? 'Complete' : assessment.status === 'finalized' ? 'Finalized' : 'Draft'}
                            </Badge>
                            <span className="text-sm text-stone-500 flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(assessment.assessment_date).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', year: 'numeric' 
                              })}
                            </span>
                          </div>
                          <h3 className="font-medium text-primary-900">
                            {ASSESSMENT_TYPES.find(t => t.value === assessment.assessment_type)?.label || 'Assessment'}
                          </h3>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {assessment.standardized_scores?.length > 0 && (
                              <span className="text-xs bg-stone-100 px-2 py-1 rounded-full">
                                {assessment.standardized_scores.length} standardized test{assessment.standardized_scores.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {assessment.non_standardized_measures?.length > 0 && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                {assessment.non_standardized_measures.length} informal measure{assessment.non_standardized_measures.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {assessment.generated_report && (
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await axios.get(`${API}/assessments/detail/${assessment.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setViewingAssessment(response.data);
                                  } catch (error) {
                                    toast.error('Failed to load assessment details');
                                  }
                                }}
                                className="text-xs bg-green-100 text-green-700 hover:bg-green-200 px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors cursor-pointer font-medium"
                                data-testid={`open-generated-report-${assessment.id}`}
                                title="Open the generated report"
                              >
                                <FileText className="w-3 h-3" /> Report ready — open it
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Show "Ready to generate" hint for assessments with data */}
                          {(assessment.standardized_scores?.length > 0 || 
                            assessment.non_standardized_measures?.length > 0 ||
                            assessment.reason_for_referral ||
                            assessment.clinical_impressions) && !assessment.generated_report && (
                            <button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const response = await axios.get(`${API}/assessments/detail/${assessment.id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  setViewingAssessment(response.data);
                                } catch (error) {
                                  toast.error('Failed to load assessment details');
                                }
                              }}
                              className="text-xs text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors cursor-pointer font-medium"
                            >
                              <Sparkles className="w-3 h-3" />
                              Ready to generate? Click here!
                            </button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await axios.get(`${API}/assessments/detail/${assessment.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setViewingAssessment(response.data);
                              } catch (error) {
                                toast.error('Failed to load assessment details');
                              }
                            }}
                            className="rounded-full"
                            data-testid={`view-assessment-${assessment.id}`}
                            title="Open assessment (and generate / view report)"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => startEdit(assessment)}
                            className="rounded-full"
                            title="Edit assessment fields"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(assessment.id)}
                            className="rounded-full text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
                </TabsContent>

                {/* Progress Reports Tab */}
                <TabsContent value="progress-reports" className="mt-0">
                  <ProgressReportsTab childId={childId} child={child} letterhead={letterhead} />
                </TabsContent>

                {/* Assessment Tracker Tab */}
                <TabsContent value="tracker" className="mt-0">
                  <AssessmentTrackerTab 
                    childId={childId} 
                    availableTests={availableTests}
                    onCreateAssessment={async (assessmentId) => {
                      // After creating from tracker, hop the user straight
                      // to the new assessment so the "Generate Report"
                      // button is right in their face — they no longer
                      // have to hunt for it across tabs.
                      try {
                        const token = localStorage.getItem('token');
                        const r = await axios.get(`${API}/assessments/detail/${assessmentId}`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        await fetchData();
                        setAssessmentsActiveTab('assessments');
                        setViewingAssessment(r.data);
                        toast.success('Assessment ready — generate the report below 👇');
                      } catch (e) {
                        // Fall back to the old behaviour if the fetch fails.
                        await fetchData();
                        setAssessmentsActiveTab('assessments');
                        toast.success('Assessment created! Open it from the list to generate the report.');
                      }
                    }}
                  />
                </TabsContent>
              </Tabs>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* View Assessment Dialog */}
      <Dialog open={!!viewingAssessment} onOpenChange={() => setViewingAssessment(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-primary" />
              Assessment Details
            </DialogTitle>
          </DialogHeader>
          
          {viewingAssessment && (
            <div className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-stone-50 rounded-xl">
                  <p className="text-xs text-stone-500">Date</p>
                  <p className="font-medium">{new Date(viewingAssessment.assessment_date).toLocaleDateString()}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-xl">
                  <p className="text-xs text-stone-500">Type</p>
                  <p className="font-medium">{ASSESSMENT_TYPES.find(t => t.value === viewingAssessment.assessment_type)?.label}</p>
                </div>
              </div>

              {viewingAssessment.reason_for_referral && (
                <div>
                  <h4 className="font-medium text-stone-700 mb-2">Reason for Referral</h4>
                  <p className="text-stone-600 text-sm bg-stone-50 p-3 rounded-xl">{viewingAssessment.reason_for_referral}</p>
                </div>
              )}

              {viewingAssessment.standardized_scores?.length > 0 && (
                <div>
                  <h4 className="font-medium text-stone-700 mb-2">Standardized Tests</h4>
                  <div className="space-y-2">
                    {viewingAssessment.standardized_scores.map((score, i) => (
                      <div key={i} className="p-3 bg-stone-50 rounded-xl">
                        <p className="font-medium text-sm">
                          {availableTests[score.test_code]?.name || score.test_code} - {score.subtest_name}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-stone-600">
                          {score.standard_score && <span>SS: <strong>{score.standard_score}</strong></span>}
                          {score.percentile_rank && <span>%ile: <strong>{score.percentile_rank}</strong></span>}
                          {score.age_equivalent && <span>AE: <strong>{score.age_equivalent}</strong></span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingAssessment.clinical_impressions && (
                <div>
                  <h4 className="font-medium text-stone-700 mb-2">Clinical Impressions</h4>
                  <p className="text-stone-600 text-sm bg-stone-50 p-3 rounded-xl whitespace-pre-wrap">{viewingAssessment.clinical_impressions}</p>
                </div>
              )}

              {/* AI Report Generation Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-stone-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    AI Report Generation
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.get(`${API}/assessments/detail/${viewingAssessment.id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setViewingAssessment(response.data);
                        if (response.data.generated_report) {
                          toast.success('Report loaded!');
                        }
                      } catch (error) {
                        toast.error('Failed to refresh');
                      }
                    }}
                    className="text-xs"
                  >
                    Refresh
                  </Button>
                </div>
                
                {/* Comprehensive Report Option */}
                <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 mb-4">
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-indigo-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-indigo-900">Comprehensive Report Generator</p>
                      <p className="text-xs text-indigo-700 mt-1">
                        Generate a detailed, section-by-section clinical report with score interpretation, 
                        GLP stage analysis, and clinical reasoning.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button
                          onClick={() => navigate(`/assessments/${childId}/${viewingAssessment.id}/report`)}
                          className="rounded-full bg-purple-600 hover:bg-purple-700"
                          size="sm"
                          data-testid="hybrid-report-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Build Report
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {viewingAssessment.generated_report ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-stone-500">
                        Generated {viewingAssessment.report_generated_at ? new Date(viewingAssessment.report_generated_at).toLocaleString() : 'recently'}
                        {viewingAssessment.report_status === 'complete_fallback' && (
                          <span className="ml-2 text-amber-600">(Summary fallback)</span>
                        )}
                      </span>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => copyReport(viewingAssessment.generated_report)}
                          className="rounded-full"
                        >
                          <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => printReport(viewingAssessment.generated_report, viewingAssessment)}
                          className="rounded-full"
                        >
                          <Printer className="w-3.5 h-3.5 mr-1" /> Print
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportDocx(viewingAssessment)}
                          className="rounded-full"
                          data-testid="export-docx-button"
                        >
                          <FileDown className="w-3.5 h-3.5 mr-1" /> Export Word
                        </Button>
                      </div>
                    </div>
                    <div className="bg-stone-50 p-4 rounded-xl max-h-60 overflow-y-auto">
                      <pre className="text-sm text-stone-700 whitespace-pre-wrap font-sans">{viewingAssessment.generated_report}</pre>
                    </div>
                  </div>
                ) : viewingAssessment.report_status === 'error' ? (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-stone-600 border-t pt-3">Quick Reports</p>
                    <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Report generation failed</p>
                          <p className="text-xs text-red-600 mt-1">
                            {viewingAssessment.report_error || 'The AI service timed out. This can happen occasionally during high traffic.'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Select value={reportStyle} onValueChange={setReportStyle}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REPORT_STYLES.map(style => (
                          <SelectItem key={style.value} value={style.value}>
                            <div>
                              <p className="font-medium">{style.label}</p>
                              <p className="text-xs text-stone-500">{style.description}</p>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      onClick={() => generateReport(viewingAssessment.id)} 
                      disabled={generatingReport}
                      className="w-full rounded-full"
                    >
                      {generatingReport ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Retrying...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Try Again
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-center text-stone-500">
                      Tip: If comprehensive reports keep failing, try "Summary" or "Parent-Friendly" for faster results.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-stone-600 border-t pt-3">Quick Reports</p>
                    <Select value={reportStyle} onValueChange={setReportStyle}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REPORT_STYLES.map(style => (
                          <SelectItem key={style.value} value={style.value}>
                            <div>
                              <p className="font-medium">{style.label}</p>
                              <p className="text-xs text-stone-500">{style.description}</p>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      onClick={() => generateReport(viewingAssessment.id)} 
                      disabled={generatingReport}
                      className="w-full rounded-full"
                      data-testid="generate-ai-report"
                    >
                      {generatingReport ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating... (30-60 seconds)
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Quick Report
                        </>
                      )}
                    </Button>
                    {generatingReport && (
                      <p className="text-xs text-center text-stone-500 mt-2">
                        Please wait while the AI generates your report. This may take up to a minute.
                      </p>
                    )}
                  </div>
                )}

                {viewingAssessment.generated_report && (
                  <div className="mt-3">
                    <Select value={reportStyle} onValueChange={setReportStyle}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REPORT_STYLES.map(style => (
                          <SelectItem key={style.value} value={style.value}>
                            {style.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline"
                      onClick={() => generateReport(viewingAssessment.id)} 
                      disabled={generatingReport}
                      className="w-full rounded-full mt-2"
                    >
                      {generatingReport ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Regenerate Report
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
